/**
 * The Parkington surface simulation.
 *
 * Everything above the soil that *decides* something: terrain, construction,
 * traffic, houses, families, the treasury, bushes, pheromone fields, and the
 * ants that surface into all of it. One `update(dt)` advances the lot.
 *
 * This is the surface half of the deterministic district core
 * (`IMPLEMENTATION-GOVERNORS.md` §5, ADR-003). It obeys the five rules in
 * `docs/determinism.md`:
 *
 *   - every stochastic decision draws from `rng`, never from `Math.random`
 *   - time comes from `simTimeMs`, never from the wall clock
 *   - no DOM, no storage, no canvas; rendering and input live in
 *     `ParkingtonLayer`, which owns this object and adds a camera to it
 *   - iteration that affects state is over arrays or sorted keys
 *   - `canonicalState()` is order-stable and includes the generators
 *
 * The practical test of all four is that this class constructs and runs in Node
 * with `Math.random` poisoned and the clock frozen, which is what
 * `tests/surface.test.ts` does.
 */

import { CommandQueue } from '../CommandQueue';
import { EventBus } from '../EventBus';
import { Canonical, hashState } from '../hashState';
import { Rng, RngState, Sfc32Rng } from '../Rng';
import {
  Command,
  CommandOutcome,
  CommandQueueState,
  FacilityTypeName,
  GovernorVerb,
  MayorVerb,
  Portal,
  ScheduleRejection,
  ScheduleResult,
  TransferableEntity,
  invalidCommand,
  readCellArgs,
  readForcePlaceArgs,
  readInterchangeArgs,
  readPaveArgs,
  readTitheArgs,
  readWithdrawStaffingArgs,
} from '@antification/protocol';
import { CarManager, CarManagerConfig, CarSnapshot } from './CarManager';
import { ConstructionManager, ConstructionSnapshot } from './ConstructionManager';
import { EconomyManager, EconomyConfig, EconomySnapshot } from './EconomyManager';
import {
  FACILITY_SPECS,
  FacilityConfig,
  FacilityManager,
  FacilitySnapshot,
  facilityGate,
} from './FacilityManager';
import {
  CycleTrade,
  MarketConfig,
  MarketManager,
  MarketSnapshot,
} from './MarketManager';
import {
  FiscalConfig,
  FiscalManager,
  FiscalSnapshot,
} from './FiscalManager';
import {
  FreightConfig,
  FreightManager,
  FreightResult,
  FreightSnapshot,
  InterchangeAxis,
  interchangeCells,
} from './FreightManager';
import {
  LabourConfig,
  LabourManager,
  LabourReading,
  LabourSnapshot,
} from './LabourManager';
import {
  MercyConfig,
  MercyManager,
  MercyResult,
  MercySnapshot,
} from './MercyManager';
import {
  Departure,
  WelfareConfig,
  WelfareManager,
  WelfareReading,
  WelfareSnapshot,
} from './WelfareManager';
import { FoodSourceManager, FoodSourceConfig, FoodSourceSnapshot } from './FoodSourceManager';
import { PheromoneGrid, PheromoneGridConfig, PheromoneSnapshot } from './PheromoneGrid';
import { HouseManager, HouseManagerConfig, HouseSnapshot } from './HouseManager';
import {
  PowerConfig,
  PowerManager,
  PowerReading,
  PowerSnapshot,
} from './PowerManager';
import {
  WaterConfig,
  WaterManager,
  WaterReading,
  WaterSnapshot,
} from './WaterManager';
import {
  SeasonManager,
  SeasonReadout,
  SeasonRecord,
  SeasonSnapshot,
  seasonOf,
} from './SeasonManager';
import { TerrainGrid, CellState, GridCoord, TerrainSnapshot } from './TerrainGrid';
import {
  DEFAULT_BLEND_WEIGHTS,
  DEFAULT_SURFACE_FORAGING,
  ParkingtonSurfaceAntController,
  BlendWeights,
  SurfaceAntsSnapshot,
  SurfaceForagingConfig,
} from './ParkingtonSurfaceAnts';
import { PortalSystem, PortalCallbacks, COLONY_COUNT } from './systems/PortalSystem';

/** Grid dimensions the game has always shipped with. */
export const SURFACE_COLS = 40;
export const SURFACE_ROWS = 30;

/**
 * Everything the surface is. See `SurfaceSim.snapshot()`.
 *
 * State, not configuration. Nothing here says how fast concrete cures or what a
 * bush is worth; those are world parameters that a season is settled *under*,
 * and the Charter (design §17) is where they will eventually live. A snapshot
 * restored under different parameters is a different world, and honestly so.
 */
export interface SurfaceSnapshot {
  tick: number;
  simTimeMs: number;
  pheromoneUpdateTimer: number;
  portalCells: Array<{ x: number; y: number }>;
  rng: RngState;
  commands: CommandQueueState;
  terrain: TerrainSnapshot;
  construction: ConstructionSnapshot;
  houses: HouseSnapshot;
  economy: EconomySnapshot;
  cars: CarSnapshot;
  food: FoodSourceSnapshot;
  pheromones: PheromoneSnapshot;
  surfaceAnts: SurfaceAntsSnapshot;
  power: PowerSnapshot;
  water: WaterSnapshot;
  facilities: FacilitySnapshot;
  market: MarketSnapshot;
  labour: LabourSnapshot;
  welfare: WelfareSnapshot;
  mercy: MercySnapshot;
  fiscal: FiscalSnapshot;
  freight: FreightSnapshot;
  season: SeasonSnapshot;
}

/**
 * What the ticker says after a departing family's name.
 *
 * §12 promises the mayor a migration with a named, measured cause —
 * *"The Harrisons left: 41 brownout-hours"* — so the news line quotes the number
 * the measure named and stops there. No adjective, no blame, and nothing about
 * where they went, because nowhere exists to go.
 *
 * The unit is **cycles**, not the design's *hours*. A budget cycle is the only
 * period this district has ever counted, the panel already prices the estate's
 * rent in `$/cy`, and inventing an equivalence between a cycle and an hour would
 * be the one number on this line that was not measured.
 *
 * The insolvency line is the string this ticker printed before there was a
 * polity, unchanged to the apostrophe: a district that never had a shortage
 * should read exactly as it always did.
 */
function departureReason(departure: Departure): string {
  switch (departure.cause) {
    case 'brownout':
      return `${departure.measure} cycles in the dark`;
    case 'dryTaps':
      return `${departure.measure} cycles without water`;
    case 'sealedGround':
      return `${departure.measure} cells of concrete at the door`;
    case 'insolvency':
    default:
      return `couldn't make ends meet`;
  }
}

export interface SurfaceSimOptions {
  cols?: number;
  rows?: number;
  /** Layer id used when emitting events. Only the client has a reason to change it. */
  layerId?: string;
}

/**
 * Trait-derived parameters, applied from `ColonyTraits`.
 *
 * These are simulation inputs, so they live here rather than on the layer.
 */
export interface SurfaceTraitParams {
  configParams: Record<string, number>;
  blendParams: BlendWeights;
  glandCapacity: number;
  glandCostPerDeposit: number;
  depositInterval: number;
  wallFollowDuration: number;
}

export class SurfaceSim {
  /**
   * The world seed every stream here descends from.
   *
   * Kept because a snapshot names the world it came from (`DistrictSnapshot.seed`)
   * and the only thing that knows the seed is whatever passed it in. Not state:
   * restoring does not read it, since every generator's position is captured
   * directly.
   */
  readonly seed: string;

  readonly grid: TerrainGrid;
  readonly construction: ConstructionManager;
  readonly cars: CarManager;
  readonly houses: HouseManager;
  readonly economy: EconomyManager;
  /**
   * The district's end of the polity grid, and the brownout ledger.
   *
   * The first of §5's five flows. It meters once per settled budget cycle and
   * sheds what does not fit under the supply; the supply is the district's own
   * interconnect until a polity allocates one. See `PowerManager`.
   */
  readonly power: PowerManager;
  /**
   * The district's mains, and the restriction ledger.
   *
   * The second of §5's five flows, metered on the same cycle as the grid. Its
   * supply is the district's own rain-fed source until a polity allocates one,
   * which makes it the only quantity in the district that the weather moves.
   * See `WaterManager`.
   */
  readonly water: WaterManager;
  /**
   * The federal estate: what the polity has built on this district's map.
   *
   * §6's facilities, arriving through the one governor verb that needs no
   * counterparty (`GovernorVerb.FORCE_PLACE`). A facility takes land the mayor
   * cannot get back, draws power and water ahead of the town's own, employs eight
   * families, records what it was supplied, and — since ADR-025 — produces
   * compute in proportion to it. See `FacilityManager`.
   *
   * Note what is *not* here: a `siteFacility()` to match `setPowerAllocation` and
   * `setRainfall`. An allocation is a scalar a district is *told*; a facility
   * changes the map, destroys bushes and drops concrete on a colony. A world
   * where that arrived by method call would be a world whose command log does not
   * contain the most consequential thing that ever happened to it, and a replayed
   * season would come out different. So a facility arrives the way a mayor's
   * pavement does: as a verb, stamped with a tick, applied at its head by every
   * peer at once (`docs/determinism.md` rule 6).
   *
   * That is also the first evidence that ADR-002's Q4 — *what authority does a
   * governor's command have* — has more than one answer in it: a governor's
   * *dials* are ambient inputs and a governor's *acts* are commands.
   */
  readonly facilities: FacilityManager;
  /**
   * The world outside, as a pair of posted prices.
   *
   * §5's World Market — *"the boundary condition standing in for the rest of the
   * world"* — reduced to the two prices this district can be reached by: what a
   * compute-unit fetches, and what a dollar of debt costs per cycle. Both are
   * national inputs in the standing `setRainfall` already had, and nothing inside
   * the simulation posts either.
   *
   * It is the sixth manager here that cannot see anything. Handed a cycle's
   * production and its running cost, it hands back dollars; that the dollars go
   * to shopkeepers, and which shopkeepers, is decided in `payProcurement` below,
   * because a market that could reach a corporation could reach a family. See
   * `MarketManager`.
   */
  readonly market: MarketManager;
  /**
   * The district's labour, and §4's third term.
   *
   * Eight families run a data centre (§6). They are hired from whoever lives
   * nearest, they drive to work through the mission system that has always driven
   * them to the shops, they are paid a wage by a polity that does not exist, and
   * the mayor can call them out. What that last sentence costs the facility is a
   * fraction in a `min`, which is the whole of the mechanic and is why §6 calls
   * staffing *"the mayors' quiet, permanent leverage"*.
   *
   * Like the two flows, this class is handed a bag of numbers and cannot reach a
   * house, a car or a colony. Unlike them, what it hands back is a list of people:
   * a roster and a payroll, which `SurfaceSim` is what pays. See `LabourManager`.
   */
  readonly labour: LabourManager;
  /**
   * The households, measured, and §10's migration.
   *
   * The last peer settled in a budget cycle, and the only one whose subject is
   * whether anybody wants to live here. It reads what the grid, the mains, the
   * market and the treasury did to each family, scores it, and names who is
   * leaving and why — *"the Harrisons left: 41 brownout-hours"*, which is §12's
   * own line and is the whole of the mechanic.
   *
   * It replaces nothing and generalises one thing: the three-cycle hardship
   * eviction that has been in the treasury since before there was a design
   * document is now one term of the sum, and still the dominant one. See
   * `WelfareManager`.
   */
  readonly welfare: WelfareManager;
  /**
   * The three things a mayor can build that pay nothing.
   *
   * §10's mercy builds: a meadow covenant, a hedgerow, a wildlife undercrossing.
   * The first thing in this district that costs the treasury money on a click rather
   * than on a policy, and the only thing in the whole build whose entire purpose is
   * to be worth nothing to the person paying for it.
   *
   * Like the estate, this class reaches the ground and nothing else. It does not
   * know that anything lives under the road it has just built a way beneath — the
   * ant controller is handed a predicate and this class is never told the predicate
   * was consulted. That asymmetry is deliberate and it is Law 7 read forwards: the
   * layer above cannot see the layer below, *including when it is trying to help*.
   *
   * See `MercyManager`.
   */
  readonly mercy: MercyManager;
  /**
   * What the district owes the world outside it.
   *
   * §8's fiscal federalism from the district's end: the bill for the power and
   * water it was delivered, the tithe a governor levies on its tax revenue, the
   * grant that is a negative tithe — and the rule that decides what any of that
   * costs. Which is the thing six ADRs have deferred: the government pool has been
   * a scoreboard since before there was a design document, and this is the manager
   * that finally reads it.
   *
   * It reads it in one place only. What a district buys from *outside* itself is
   * paid from its receipts, and road maintenance is funded from what is left; UBI
   * is untouched, because a transfer to the district's own households comes back as
   * tax on what they spend. §8 is explicit about which line gives way — *"cut
   * infrastructure (roads decay, commerce slows — the existing death-spiral)"* — and
   * a mercy build competes with it like any other outside purchase, which is
   * ADR-020 D129's deferred option arriving in the ADR it was deferred to.
   *
   * Like the flows, this class is handed numbers and can reach nothing: two
   * lifetime meter totals and one lifetime tax figure, in, dollars out. See
   * `FiscalManager`.
   */
  readonly fiscal: FiscalManager;

  /**
   * The district's corridor: its interchanges, and the freight that crosses them.
   *
   * §5's *"District maps gain interchanges: edge structures (2 cells) where
   * national freight enters and leaves the map"*, and the second half of that
   * section's promise — *"a district on a trade corridor hosts transit traffic it
   * didn't generate: noise, congestion, road wear, and ant deaths from trucks it
   * never ordered."*
   *
   * The manager holds the structures, the dial and the ledger. The trucks
   * themselves are `CarManager`'s, which is the whole point: a leg is a vehicle on
   * the district's roads at the district's road quality, and everything §5 files
   * under *"consequences that fall out for free"* falls out of that one decision
   * rather than out of anything written here.
   *
   * Like the flows and the account, this class can reach the ground and nothing
   * else. What a truck ran over between two interchanges is not a question it can
   * ask. See `FreightManager`.
   */
  readonly freight: FreightManager;
  readonly foodSources: FoodSourceManager;
  readonly pheromones: PheromoneGrid;
  readonly surfaceAnts: ParkingtonSurfaceAntController;
  readonly portalSystem: PortalSystem;

  /**
   * The season clock and the district's Chronicle.
   *
   * Here rather than one level up because the Chronicle is the public record and
   * Law 7 says it may never carry a fact about a colony. A settler that lives in
   * the surface, is handed a bag of numbers, and holds no reference to anything
   * else cannot record one. See `SeasonManager`'s header.
   */
  readonly seasons = new SeasonManager();

  /** Where each colony's entrance sits. Index is the colony index. */
  readonly portalCells: Array<{ x: number; y: number }> = [];

  /** Portals this surface exposes to other layers. */
  readonly portals: Portal[] = [];

  /**
   * The district's generator.
   *
   * Subsystems get named forks rather than sharing this stream. A fork depends
   * on the seed lineage and not on how far the parent has advanced, so adding a
   * car cannot shift which family moves in next — which is what makes a change
   * to one subsystem reviewable against the golden masters.
   */
  private readonly rng: Rng;

  /**
   * Everything the mayor has asked for and the world has not yet done.
   *
   * The only way in. Nothing outside the simulation calls a manager directly:
   * an action is stamped with a tick, queued, and applied at the head of that
   * tick by every peer at once (`docs/determinism.md` rule 6). Owned here
   * rather than by the client because a pending command is *state* — a world
   * holding a `PAVE` for tick 4,100 is not the world that is not.
   */
  private readonly commands = new CommandQueue();

  /** Returned by an ordinary tick, so nothing is allocated when nothing is due. */
  private static readonly NO_COMMANDS: readonly CommandOutcome[] = [];

  /**
   * The bus this district reports on.
   *
   * Held because one thing the surface does is not a manager's: pouring a
   * facility's slab emits the same `CONCRETE_POURED` the mayor's own concrete
   * does, and the class that decides where a facility goes is deliberately unable
   * to emit anything (`FacilityManager`). See `forcePlace`.
   */
  private readonly events: EventBus;

  /** Sim-milliseconds elapsed. The simulation's only clock. */
  private simTimeMs = 0;

  /** Ticks advanced. Counted, not derived, so a rate change mid-world is survivable. */
  private tickCount = 0;

  // --- Pheromone field update ---
  private readonly PHEROMONE_UPDATE_INTERVAL = 100;
  private pheromoneUpdateTimer = 0;

  // --- Surface ant tunables (trait-driven) ---
  private readonly PHEROMONE_DEPOSIT_INTERVAL = 150;
  private readonly TRAIL_FOLLOW_THRESHOLD = 0.05;
  private readonly WALL_FOLLOW_DURATION = 2000;
  private glandCapacity = 1.0;
  private glandCostPerDeposit = 0.08;
  private blendWeights: BlendWeights = { ...DEFAULT_BLEND_WEIGHTS };
  private surfaceForagingConfigs: Map<number, SurfaceForagingConfig> = new Map();

  constructor(events: EventBus, seed: string, options: SurfaceSimOptions = {}) {
    const cols = options.cols ?? SURFACE_COLS;
    const rows = options.rows ?? SURFACE_ROWS;
    const layerId = options.layerId ?? 'parkington';

    this.seed = seed;
    this.events = events;
    this.rng = new Sfc32Rng(`${seed}/parkington`);
    const now = () => this.simTimeMs;

    this.grid = new TerrainGrid(cols, rows);
    this.construction = new ConstructionManager(this.grid, events, now);
    this.cars = new CarManager(this.grid, this.rng.fork('cars'));
    this.houses = new HouseManager(this.grid, events, this.rng.fork('houses'), now);
    this.economy = new EconomyManager(this.rng.fork('economy'));
    // No fork for either: a shortfall is arithmetic and which home it lands on
    // is a rotation, so neither flow draws anything.
    this.power = new PowerManager();
    this.water = new WaterManager();
    // No fork either: where a facility goes is a governor's decision and what it
    // gets is arithmetic, so there is nothing here to roll.
    this.facilities = new FacilityManager(this.grid);
    // Nor here: who gets a job is decided by where they live, and ties by who
    // moved in first.
    this.labour = new LabourManager();
    // Nor here, and this one is the pointed case: whether a family gives up is a
    // sum over what was done to them, so there is no luck in it at all. A
    // migration that rolled would be a migration nobody could argue with.
    this.welfare = new WelfareManager();
    // Nor here, and it is the least surprising of the five: a mayor decides where
    // every covenant, hedgerow and crossing goes, and a calendar decides when a bush
    // comes in. There is nothing in mercy to roll.
    this.mercy = new MercyManager(this.grid);
    // Nor here, and it is the flattest case of all: a bill is a multiplication and
    // a tithe is a percentage. There has never been anything to roll in a ledger.
    this.fiscal = new FiscalManager();
    // Nor here, and §5 is explicit about why: the World Market's *"bounded
    // noise"* is a schedule the polity publishes, not a die this district throws.
    // A price arrives posted or it is the price of last resort.
    this.market = new MarketManager();
    // The treasury borrows at the market's rate from the first cycle, not from
    // the first posting. The two defaults agree, so this changes nothing today
    // and is here so that they cannot silently stop agreeing.
    this.applyBondYield();
    // Nor here, and this one is a decision rather than an omission. A corridor
    // that drew would be traffic no governor could plan against and no mayor
    // could blame anybody for — Law 3 lets physics be stochastic and does not let
    // politics be. Which pair a leg runs between is a cursor; how many run is an
    // accumulator; when a truck arrives is the roads.
    this.freight = new FreightManager(this.grid);
    this.foodSources = new FoodSourceManager(this.grid, this.rng.fork('food'));
    this.pheromones = new PheromoneGrid(cols, rows);

    // Wire the housing market. Once, for the life of the district, like the three
    // callbacks below it — the questions it answers are ones `HouseManager` has
    // no way to ask.
    this.houses.market = this.housing;

    // And which ground is spoken for, on the same terms. A deeded cell is not a
    // building site, and `HouseManager` has no way to find that out.
    this.houses.land = this.land;

    // The one stage of a season settlement that does anything besides report.
    // Wired here rather than owned by the settler because levying a tithe means
    // touching a treasury, and a settler that could reach a treasury could reach a
    // colony — which is the whole of `SeasonManager`'s argument about Law 7.
    this.seasons.settles('fiscal', () => this.settleFiscal());
    this.seasons.settles('construction', () => this.settleConstruction());

    // Wire economy visit payment callback
    this.houses.onFamilyVisit = (familyId, houseId) => {
      this.economy.processVisitPayment(familyId, houseId, this.houses);
    };

    // Wire business grant callback — always pays, debt consequences via interest
    this.houses.onCorpSpawning = () => {
      this.economy.payBusinessGrant();
      return true;
    };

    this.surfaceAnts = new ParkingtonSurfaceAntController({
      grid: this.grid,
      cars: this.cars,
      houses: this.houses,
      foodSources: this.foodSources,
      pheromones: this.pheromones,
      portals: this.portals,
      portalCells: this.portalCells,
      events,
      layerId,
      getSurfaceForagingConfig: (colonyIndex) => this.getSurfaceForagingConfig(colonyIndex),
      rng: this.rng.fork('surface-ants'),
      now,
      // The undercrossing, and the whole of it. A closure over the manager rather
      // than the manager itself: the ants may ask whether a cell has a way under it
      // and may not ask anything else, and nothing they do can be observed from the
      // other side of the call.
      isSheltered: (x, y) => this.mercy.shelters(x, y),
      trailFollowThreshold: this.TRAIL_FOLLOW_THRESHOLD,
      blendWeights: this.blendWeights,
      // Trait-mutable; rewritten in place through `surfaceAnts.tunables`.
      pheromoneDepositInterval: this.PHEROMONE_DEPOSIT_INTERVAL,
      wallFollowDuration: this.WALL_FOLLOW_DURATION,
      glandCapacity: this.glandCapacity,
      glandCostPerDeposit: this.glandCostPerDeposit,
    });

    this.portalSystem = new PortalSystem(
      this.grid, this.pheromones, this.surfaceAnts, this.rng.fork('portals')
    );

    // Connect cars to houses
    this.cars.setHouseManager(this.houses);
    // And to the gates of whatever the polity has built here, so a commute is an
    // ordinary trip to an ordinary cell.
    this.cars.setFacilityGates({ gateOf: (id) => this.facilities.gateOf(id) });
  }

  /** Sim-milliseconds elapsed. */
  get elapsedMs(): number {
    return this.simTimeMs;
  }

  /** Ticks advanced. */
  get tick(): number {
    return this.tickCount;
  }

  /**
   * Place the colony entrances and register the portals that reach them.
   *
   * Separate from the constructor because it emits portals a caller has to wire
   * into a `LayerManager`, and because a load may replace the placement.
   */
  setupPortals(): void {
    const callbacks: PortalCallbacks = {
      registerPortal: (portal) => { this.portals.push(portal); },
      getPortals: () => this.portals,
    };
    this.portalSystem.setupPortals(this.portalCells, callbacks);
  }

  /**
   * Receive an entity — an ant surfacing — from another layer.
   *
   * Returns whether the surface took it. Since ADR-028 it can say no: an entrance
   * with a facility's foundation over it is closed, and the ant stays below.
   */
  receiveEntity(entity: TransferableEntity, fromPortal: string): boolean {
    return this.portalSystem.receiveEntity(entity, fromPortal);
  }

  /**
   * Whether a colony's entrance has been sealed by a federal slab.
   *
   * The district's public read of the seal, for the client that draws the hole and
   * the mouse that lets somebody click it. Both would otherwise be showing a way
   * down that is not there.
   *
   * There is nothing to hide here and it is worth saying why, because everything
   * in this direction usually is: this reports a fact about *the ground* — a cell
   * of concrete somebody poured — and holds no word about what is underneath it.
   * A sealed entrance and an entrance whose colony died last season read the same.
   */
  isEntranceSealed(colonyIndex: number): boolean {
    return this.surfaceAnts.entranceIsSealed(colonyIndex);
  }

  // --- Commands -------------------------------------------------------------

  /**
   * The earliest tick a new command may be stamped with.
   *
   * Equal to `tick` between updates: the next `update()` drains the tick it is
   * about to advance past, so a command handed in now lands on it.
   */
  earliestSchedulableTick(): number {
    return this.commands.earliestSchedulableTick();
  }

  /** Commands accepted but not yet applied, in canonical order. */
  pendingCommands(): Command[] {
    return this.commands.pending();
  }

  /**
   * Accept a command for its stamped tick.
   *
   * Validation happens here rather than in the queue because only a simulation
   * knows what a verb means, and it happens at the door rather than at
   * application time because anything admitted is replayed for the rest of the
   * world's life. A rejection is information for the caller, not an error: it
   * says the issuer and the world disagree about something.
   */
  schedule(command: Command): ScheduleResult {
    const invalid = this.validate(command);
    if (invalid) return invalid;
    return this.commands.schedule(command);
  }

  private validate(command: Command): ScheduleRejection | null {
    switch (command.verb) {
      case MayorVerb.PAVE: {
        const args = readPaveArgs(command.args);
        if (!args) return invalidCommand('PAVE needs integer x and y');
        // Grid bounds are world configuration, not state, so judging a future
        // command against today's grid is safe: the map does not resize.
        if (!this.grid.getCell(args.x, args.y)) {
          return invalidCommand(`PAVE names no cell: ${args.x},${args.y}`);
        }
        return null;
      }
      case MayorVerb.WITHDRAW_STAFFING: {
        const args = readWithdrawStaffingArgs(command.args);
        if (!args) return invalidCommand('WITHDRAW_STAFFING needs a positive integer facility');
        // Whether a facility with that id is *standing* is deliberately not
        // checked, for the reason `FORCE_PLACE`'s footprint is not: a command may
        // be stamped seasons ahead of the tick it applies on, and a strike called
        // against a facility that has not been sited yet is a strike that does
        // nothing rather than a malformed sentence.
        return null;
      }
      case MayorVerb.RESTORE_STAFFING:
        // No arguments to check. §18 gives it none: ending a strike is a decision
        // about working, not about a building.
        return null;
      case MayorVerb.COVENANT:
      case MayorVerb.HEDGEROW:
      case MayorVerb.UNDERCROSS: {
        // The three mercy builds name a cell and nothing else, and the only thing
        // checked at the door is that the cell exists. Whether it is natural,
        // already deeded, already planted or paved yet are all questions about the
        // map *at the tick the command applies* — the same distinction `PAVE` and
        // `FORCE_PLACE` draw. An undercrossing stamped for a tick by which the
        // concrete has been poured is a perfectly sensible thing to queue.
        const args = readCellArgs(command.args);
        if (!args) return invalidCommand(`${command.verb} needs integer x and y`);
        if (!this.grid.getCell(args.x, args.y)) {
          return invalidCommand(`${command.verb} names no cell: ${args.x},${args.y}`);
        }
        return null;
      }
      case MayorVerb.INTERCHANGE: {
        const args = readInterchangeArgs(command.args);
        if (!args) {
          return invalidCommand('INTERCHANGE needs integer x, y and an axis of h or v');
        }
        // The named cell only, and not the second one, on the same terms as
        // `FORCE_PLACE`'s footprint: whether the pair fits and whether both cells
        // are road are questions about the map at the tick this applies on.
        if (!this.grid.getCell(args.x, args.y)) {
          return invalidCommand(`INTERCHANGE names no cell: ${args.x},${args.y}`);
        }
        return null;
      }
      case GovernorVerb.FORCE_PLACE: {
        const args = readForcePlaceArgs(command.args);
        if (!args) {
          return invalidCommand('FORCE_PLACE needs a known facility and integer x, y');
        }
        // The origin is checked, and deliberately not the footprint. Whether a
        // 6×4 rectangle fits, and whether anything is already standing in it, are
        // questions about the map *at the tick the command applies* — and a
        // command may be stamped seasons ahead of that. Refusing at the door on
        // today's map would reject a siting that will be perfectly legal, and
        // accepting one that will not be is what `applied: false` is for.
        if (!this.grid.getCell(args.x, args.y)) {
          return invalidCommand(`FORCE_PLACE names no cell: ${args.x},${args.y}`);
        }
        return null;
      }
      case GovernorVerb.SET_TITHE: {
        const args = readTitheArgs(command.args);
        if (!args) return invalidCommand('SET_TITHE needs a finite rate');
        // The Charter's cap is deliberately not checked here. It is a world
        // parameter, and a command may be stamped seasons ahead of the tick it
        // applies on — so a rate that is illegal today and legal under the Charter
        // this world adopts next season is a well-formed sentence. Same distinction
        // `FORCE_PLACE`'s footprint draws, and the refusal is `applied: false`.
        return null;
      }
      default:
        return invalidCommand(`unknown verb: ${command.verb}`);
    }
  }

  /**
   * Apply everything due at the head of this tick.
   *
   * Before the clock advances, so a command lands at the sim-time the tick
   * starts at — which is where a click used to land when the client applied it
   * between ticks, and why the golden masters recorded before the queue existed
   * did not move when it arrived.
   */
  private applyDueCommands(): readonly CommandOutcome[] {
    const due = this.commands.drain(this.tickCount);
    if (due.length === 0) return SurfaceSim.NO_COMMANDS;

    const outcomes: CommandOutcome[] = [];
    for (const command of due) {
      outcomes.push({ command, applied: this.applyCommand(command) });
    }
    return outcomes;
  }

  /** Returns whether the verb changed anything. */
  private applyCommand(command: Command): boolean {
    switch (command.verb) {
      case MayorVerb.PAVE: {
        const args = readPaveArgs(command.args);
        // Validated on the way in; a replayed log could still carry rubbish, and
        // a settlement worker must not die of someone else's bad record.
        if (!args) return false;
        // Federal ground is not the mayor's to develop (§6: *"the land
        // unavailable to the mayor"*). The command is well-formed and correctly
        // ordered and it does nothing, which is exactly what a `PAVE` on wet
        // concrete already does — see `CommandOutcome.applied`.
        if (this.facilities.covers(args.x, args.y) !== null) return false;
        // Nor is deeded ground, and the refusal is in the same line for the same
        // reason: §10's covenant locks a cell `NATURAL` *forever*, and the only
        // enforcement worth having is at the one place the ground changes.
        //
        // Note whose promise this is. Above, a governor's slab stops the mayor;
        // here, the mayor's own signature does. There is no verb in this
        // vocabulary that lifts one, which is what "irreversible" has to mean if
        // it is going to mean anything — a covenant a mayor could quietly revoke
        // in a bad season is not restraint, it is an intention.
        if (this.mercy.isDeeded(args.x, args.y)) return false;
        return this.construction.handleCellClick(args.x, args.y);
      }
      case MayorVerb.WITHDRAW_STAFFING: {
        const args = readWithdrawStaffingArgs(command.args);
        if (!args) return false;
        // The simulation supplies the one fact the labour market cannot see —
        // whether that slab is on the map — and the market decides the rest.
        return this.labour.withdraw(
          args.facility,
          this.facilities.getFacility(args.facility) !== undefined
        );
      }
      case MayorVerb.RESTORE_STAFFING:
        return this.labour.restore();
      case MayorVerb.COVENANT: {
        const args = readCellArgs(command.args);
        if (!args) return false;
        // The one fact the works cannot see: whether somebody already lives on it.
        // The same predicate a `FORCE_PLACE` is handed, for the same reason.
        return this.buildMercy(
          this.mercy.deed(args.x, args.y, (cx, cy) => this.houses.occupiesCell(cx, cy))
        );
      }
      case MayorVerb.HEDGEROW: {
        const args = readCellArgs(command.args);
        if (!args) return false;
        return this.buildMercy(
          this.mercy.plant(args.x, args.y, this.economy.getCyclesSettled())
        );
      }
      case MayorVerb.UNDERCROSS: {
        const args = readCellArgs(command.args);
        if (!args) return false;
        return this.buildMercy(this.mercy.cross(args.x, args.y));
      }
      case MayorVerb.INTERCHANGE: {
        const args = readInterchangeArgs(command.args);
        if (!args) return false;
        return this.buildInterchange(this.freight.build(args.x, args.y, args.axis));
      }
      case GovernorVerb.FORCE_PLACE: {
        const args = readForcePlaceArgs(command.args);
        if (!args) return false;
        return this.forcePlace(args.facility, args.x, args.y);
      }
      case GovernorVerb.SET_TITHE: {
        const args = readTitheArgs(command.args);
        if (!args) return false;
        return this.setTithe(args.rate);
      }
      default:
        return false;
    }
  }

  /**
   * Seize a rectangle and pour a slab on it.
   *
   * The two halves are separated on purpose. `FacilityManager` decides whether
   * the rectangle can be sealed and seals it; *this* method knows what is
   * standing on the ground and what is living under it, and it is the one that
   * pours the concrete in the sense the rest of the game means by that.
   *
   * A `CONCRETE_POURED` per sealed cell is not decoration. It is the same event
   * the mayor's own pavement emits, and the colonies have been listening to it
   * since long before there was a polity: `DamageSystem.handleConcretePour`
   * collapses tunnels near the pour and kills some of the ants in them. So a slab
   * dropped across a colony's entrance is a catastrophe below and a line item
   * above, through a mechanic nobody had to add — which is the whole recursion the
   * design is about, arriving for free because both layers were already built to
   * be honest about the same physics.
   *
   * Nothing here is charged for. §7's statutory compensation, the readiness bonus
   * a volunteered site would have earned, the local-hire agreement, the mayor who
   * stops answering the next notice — all of it is Phase 3, and Law 6's *later*
   * cost is therefore missing while its *now* cost is not. `docs/facilities.md`
   * says so plainly rather than letting the arithmetic look balanced.
   */
  private forcePlace(facility: FacilityTypeName, x: number, y: number): boolean {
    const result = this.facilities.place(facility, x, y, this.tickCount, (cx, cy) =>
      this.houses.occupiesCell(cx, cy)
    );
    if (!result.ok) return false;

    let condemned = 0;
    for (const cell of result.sealed) {
      // Whatever the district had bought for this cell is void. §10 promises a
      // covenant is *"irreversible except by condemnation (Charter-gated)"*, and
      // there is no Charter here, so the gate is missing and the seizure is not:
      // deeded ground seals exactly as easily as anybody's meadow. The count is the
      // whole consequence.
      if (this.mercy.condemn(cell.x, cell.y)) condemned++;
      this.events.emit('CONCRETE_POURED', { x: cell.x, y: cell.y });
    }

    if (condemned > 0) {
      // The one line in the ticker about it, and it is the district's own voice.
      // Nothing is charged, nothing is compensated, and the sentence draws no
      // conclusion — the register Parkington uses about everything that is done to
      // it. A governor reading the Chronicle will find `condemnedCells`; the mayor
      // gets this.
      this.houses.addNews(
        `Federal siting took ${condemned} protected ` +
        `${condemned === 1 ? 'cell' : 'cells'}`,
        'economy'
      );
    }
    return true;
  }

  /**
   * Turn the extraction dial, and tell the district it was turned.
   *
   * The two halves are separated the way the estate's and the works' are:
   * `FiscalManager` decides whether the rate is inside the Charter's cap, and
   * *this* object is the only thing that can put a sentence in front of a mayor.
   *
   * The ticker line is the whole of the district's notification. There is no mail,
   * no Correspondence tab and no Ledger Dock — those are §12's and Phase 4's — so a
   * mayor finds out about a levy the same way they find out about a pothole, which
   * is honest about how much voice this build gives them. What the line does not do
   * is warn: it says the rate, and what the rate will cost is arithmetic the mayor
   * can do and nothing here does for them.
   */
  private setTithe(rate: number): boolean {
    if (!this.fiscal.setTitheRate(rate)) return false;
    const pct = Math.round(rate * 100);
    this.houses.addNews(
      pct >= 0
        ? `Polity set the tithe at ${pct}% of tax revenue`
        : `Polity set a grant of ${-pct}% of tax revenue`,
      'economy'
    );
    return true;
  }

  /**
   * Pay the polity for the power and water this cycle delivered.
   *
   * §8's *"Power & water bills | − | Metered at polity prices"*. Here rather than in
   * either manager for the reason the wages and the rent are: the meters know what
   * they delivered, the treasury holds the money, the fiscal account holds the
   * price, and no two of the three can see each other.
   *
   * After both meters, so the bill prices the cycle that has just been read, and
   * after the estate's rent, so the rent is in the pool before the invoice leaves
   * it. The order does not change the balance — a pool is one number and these are
   * all additions — but it is the order a reader would expect and there is no reason
   * to surprise them.
   *
   * Both prices are unconditional on everything except delivery. A district in a
   * brownout is billed for less, which is the only place in this build where a
   * shortage is worth money to the district it happens to, and the saving does not
   * compensate: the households that sat in the dark are counted somewhere else
   * entirely (ADR-018), and they leave.
   */
  private payUtilities(): void {
    const bill = this.fiscal.bill(
      this.power.getLedger().deliveredKwCycles,
      this.water.getLedger().deliveredM3Cycles
    );
    this.economy.payUtilities(bill);
  }

  /**
   * Settle the season's tithe. The `fiscal` stage of §4's settlement.
   *
   * The first thing in this game that moves money at a season boundary, and the
   * whole of it is a percentage of a number the district measured itself: the tax
   * it collected over the ninety cycles since the last levy. Law 1 is satisfied by
   * the shape rather than by care — there is no term here that is not a sum over
   * settled transactions, and a rate of zero moves nothing at all.
   *
   * Run at every season boundary and counted at none of them where the rate is
   * zero. A district under a governor who never asks it for anything settles exactly
   * as a district with nobody above it does — same figures, same hash, no block in
   * its Chronicle — which is what makes the appearance of the block mean something.
   */
  private settleFiscal(): void {
    const due = this.fiscal.levy(this.economy.getTotalTaxCollected());
    this.economy.remitTithe(due);
    if (due === 0) return;
    this.houses.addNews(
      due > 0
        ? `Tithe remitted: $${Math.round(due)}`
        : `Grant received: $${Math.round(-due)}`,
      'economy'
    );
  }

  // --- The district's own works ---------------------------------------------

  /**
   * Pay for a mercy build, or find out there was nothing to pay for.
   *
   * The two halves are separated for the reason the estate's are: `MercyManager`
   * decides whether the ground can carry a covenant, a bush or a way under it, and
   * *this* object is the only thing that can reach a treasury. A refusal costs
   * nothing and reports `applied: false`, which is the same standing a `PAVE` on wet
   * concrete has and matters more here — these are brush tools, a drag passes over
   * the same cell repeatedly, and a mayor must not be billed three times for one
   * covenant.
   *
   * Nothing checks whether the district can afford it. That is not an oversight:
   * this treasury has never refused a payment in its life (`EconomyManager`'s
   * header), and inventing the first affordability check in the game for the one
   * spending line that buys nothing would be a strange place to start.
   *
   * What the second line does instead is commit the money against the district's
   * receipts (ADR-021), so the click always lands and the road budget pays for it
   * until the receipts have caught up. Two calls for one act, because they are two
   * different facts about it: the dollars left the pool, and somebody outside the
   * district is owed them. `FiscalManager.commitWorks` says why a covenant is in the
   * same rule as a power bill rather than in a rule of its own.
   */
  private buildMercy(result: MercyResult): boolean {
    if (!result.ok) return false;
    this.economy.payForWorks(result.cost);
    this.fiscal.commitWorks(result.cost);
    return true;
  }

  /**
   * Pay for an interchange the corridor has just accepted.
   *
   * The same two lines a mercy build takes and for the same reason: concrete and
   * ramps are bought from outside the district, so the dollars leave the pool and
   * the commitment claims the road budget until receipts have covered it. A
   * corridor built in a good season is absorbed in four cycles; one built behind a
   * levy costs a month of maintenance before a single truck has crossed it.
   *
   * Which is the honest shape of the bargain §12 describes — *"a well-placed
   * interchange makes the district a corridor (tolls, but trucks)"* — with a third
   * clause the design leaves implicit: and potholes first.
   */
  private buildInterchange(result: FreightResult): boolean {
    if (!result.ok) return false;
    this.economy.payForInterchange(result.cost);
    this.fiscal.commitWorks(result.cost);
    this.houses.addNews('Interchange opened — freight may cross the district', 'economy');
    return true;
  }

  /**
   * Bring in the hedgerows that have come of age.
   *
   * Two steps the measure cannot take itself: a bush is the ground's
   * (`TerrainGrid.plantBush`) and its fruit is `FoodSourceManager`'s, and
   * `MercyManager` can reach neither. It hands over a list of cells in ascending
   * order and finds out how many took — the same split as the estate's rent and the
   * market's payroll.
   *
   * A sapling whose cell was paved or sealed while it was growing does not take, and
   * is counted as lost rather than silently dropped. §10 says *"nurture is possible,
   * undoing is not"* about pavement; it turns out nurture can be undone, by the
   * person who paid for it, with a brush. That is worth a number.
   */
  private settleMercy(): void {
    const due = this.mercy.settle(this.economy.getCyclesSettled());
    if (due.length === 0) return;

    let grown = 0;
    for (const cell of due) {
      if (!this.grid.plantBush(cell.x, cell.y)) continue;
      // The bush exists; now it can bear. Order follows the cell order the measure
      // sorted, so food source ids are assigned identically on every peer.
      this.foodSources.plantAt(cell.x, cell.y);
      grown++;
    }

    this.mercy.recordGrown(grown);
    this.mercy.recordLost(due.length - grown);

    if (grown > 0) {
      this.houses.addNews(
        `${grown} ${grown === 1 ? 'hedgerow has' : 'hedgerows have'} come in`,
        'economy'
      );
    }
  }

  /**
   * Advance the surface by one tick.
   *
   * Commands first, at the tick's own sim-time. Then the order is load-bearing:
   * construction settles the ground, bushes ripen on it, families decide
   * against that state — on the power the grid last allocated them — the
   * treasury settles their transactions, the grid and the mains meter the cycle
   * the treasury just closed, traffic moves at the road quality the treasury
   * just set, and the ants meet whatever traffic resulted.
   *
   * Returns what this tick's commands did, for a client that predicted them.
   * The return value is a readout, not state: ignoring it changes nothing.
   */
  update(dt: number): readonly CommandOutcome[] {
    const outcomes = this.applyDueCommands();

    this.tickCount++;
    this.simTimeMs += dt;

    this.construction.update(dt);
    this.foodSources.update(dt);

    const counts = this.grid.countCellsByState();
    const hasPaved = counts[CellState.POURED] > 0 ||
                     counts[CellState.CURED] > 0 ||
                     counts[CellState.PAINTED] > 0;

    // Sites the grid shed at the last metered cycle do not build during this
    // one. The argument is omitted while nothing is shed, which is every tick
    // of a district that fits under its supply. `commuting` is omitted on the
    // same terms: while nobody holds a federal job there is nothing to ask.
    this.houses.update(
      dt,
      hasPaved,
      this.power.shedding ? this.power : undefined,
      this.labour.employed ? this.commuting : undefined
    );

    const cyclesBefore = this.economy.getCyclesSettled();
    // The fiscal account goes in as the cycle's spending limit. The treasury still
    // decides what it would like to spend; what it may is a question about money the
    // district owes people it cannot see, and it has never been able to see them.
    // `hasPaved` goes in as the fourth argument, and it is the whole of ADR-024
    // Q59: a district with no pavement neither spends its road budget nor decays.
    this.economy.update(dt, this.houses, this.fiscal, hasPaved);
    // The flows meter the district's own fiscal period rather than a clock of
    // their own: one counter, already cadence-invariant, already tested as such.
    // Both read the same cycle, in a fixed order, and neither can see the other.
    if (this.economy.getCyclesSettled() > cyclesBefore) {
      this.power.meter(this.readPower());
      this.water.meter(this.readWater());
      // Third, and before the estate reads it: hiring is what decides the term
      // the next line takes a `min` over. The market cannot see either flow and
      // neither flow can see it.
      this.labour.settle(this.readLabour());
      // Last, and only ever handed three numbers. The estate finds out what it
      // was supplied; none of the three suppliers finds out what it supplied.
      const output = this.facilities.meter(
        this.power.getFacilityFraction(),
        this.water.getFacilityFraction(),
        this.labour.getStaffedFraction()
      );
      // And then the world buys it. The estate hands over what it made and what
      // making it cost; the market hands back the share of that cost §6 spends
      // locally. Neither can see the other's subject: the estate does not know
      // what a compute-unit fetches and the market does not know what a data
      // centre is.
      this.payProcurement(this.market.settleCycle(output.computeUnits, output.operatingCost));
      this.payWages();
      this.payLease();
      // The corridor's two, together, on the legs that completed since the last
      // cycle: the toll into the pool beside the rent, and the wear off the road
      // the maintenance budget has just finished repairing.
      this.payTolls();
      // And then the invoice for what the two meters just read, after the rent that
      // helps pay it. §8's first new subtraction, and the first thing in this
      // treasury's life that a district's own growth makes more expensive.
      this.payUtilities();
      // The district's own works, settled with the district's own money and before
      // anybody is asked how they are. A hedgerow that comes in this cycle is a bush
      // for the rest of it; ordering it after the measure below would make it a bush
      // for the rest of the *next* one, which is a cycle of fruit nobody would ever
      // notice and a rule nobody could justify.
      this.settleMercy();
      // Last of everything. A household is asked how it is doing once every
      // other thing that could happen to it this cycle has: the treasury has
      // taxed it and paid its income, the grid has decided whether its lights
      // were on, the mains whether its taps ran, and the payroll has landed. A
      // measure taken any earlier would be measuring a cycle that was not over.
      this.settleWelfare();
      // Last of the cycle, and after the wear: this cycle's trucks set off on the
      // road quality this cycle left them, which is the arrow §5 draws — freight
      // capacity is *"routes × road quality × congestion"*, so a corridor its own
      // traffic has worn down carries its next legs more slowly.
      this.dispatchFreight();
      // And the polity's own lorries, after the district's transit and on the
      // same road quality: §7's construction convoys, aimed at a building site
      // rather than across the map. Ordered after the corridor because a crossing
      // was routed before a slab was seized in every district that has both, and
      // because the two compete for the same room at the same gates — a district
      // whose interchange is blocked lapses the transit leg first.
      this.dispatchConvoys();
    }

    // Apply road quality to car speed
    this.cars.updateConfig({ SPEED_MULTIPLIER: this.economy.getRoadSpeedMultiplier() });

    if (hasPaved) {
      this.cars.update(dt);
      // Immediately, so the tray is empty at every boundary a snapshot can be
      // taken on. See `collectFreight`.
      this.collectFreight();
    }

    this.surfaceAnts.update(dt);

    // Pheromone field update. Throttled to every 100 sim-ms, but the whole
    // elapsed span is handed to the field, so throttling changes when decay is
    // computed and not how much of it there is.
    this.pheromoneUpdateTimer += dt;
    if (this.pheromoneUpdateTimer >= this.PHEROMONE_UPDATE_INTERVAL) {
      this.pheromones.update(this.pheromoneUpdateTimer, (x, y) => {
        const cell = this.grid.getCell(x, y);
        if (!cell) return { evapMod: 1, diffusionMod: 1 };
        // Pavement: trails fade fast on hot concrete, stay narrow
        if (cell.state === CellState.CURED || cell.state === CellState.PAINTED) {
          return { evapMod: 0.67, diffusionMod: 0.5 };
        }
        // Bush/vegetation areas: trails persist longest near food, spread wide
        if (cell.state === CellState.NATURAL && cell.vegetationType === 'bush') {
          return { evapMod: 1.67, diffusionMod: 1.3 };
        }
        // Other vegetation: trails persist, normal spread
        if (cell.state === CellState.NATURAL && cell.vegetationType !== 'none') {
          return { evapMod: 1.25, diffusionMod: 1.0 };
        }
        // Soil/grass: normal
        return { evapMod: 1, diffusionMod: 1 };
      });
      this.pheromones.updateFoodOdor(this.foodSources.getAllSourceData());
      this.pheromoneUpdateTimer = 0;
    }

    // Last, so a season is closed on a district that has finished its tick.
    const closed = this.seasons.settleIfDue(
      this.economy.getCyclesSettled(),
      () => this.readSeason()
    );
    if (closed) this.reportSeason(closed);

    return outcomes;
  }

  // --- The grid ------------------------------------------------------------

  /**
   * What draws power, as the meter is allowed to see it.
   *
   * Taken once per budget cycle, which is what makes one walk of the house list
   * and one cell count affordable. Like the season readout, it is a bag of ids
   * and numbers rather than a district: a flow has no business forming an
   * opinion about traffic, bushes, or anything under the soil.
   *
   * A house draws from the moment it starts going up, and a vacant one still
   * has a meter. What changes with occupancy is which class it is shed in.
   */
  private readPower(): PowerReading {
    const sites: number[] = [];
    const premises: number[] = [];
    const homes: number[] = [];

    for (const house of this.houses.getHouses()) {
      if (!house.built) sites.push(house.id);
      else if (house.occupant === 'corporation') premises.push(house.id);
      else homes.push(house.id);
    }

    return {
      sites,
      premises,
      homes,
      // Painted cells only. A finished lot is the one with lighting on it;
      // poured and curing concrete is the construction site's own load.
      litCells: this.grid.countCellsByState()[CellState.PAINTED],
      // One number, and zero for every district in this repository's golden
      // masters. A facility is not in any of the three id lists above: the
      // district cannot shed it, so there is nothing per-building to record.
      facilityKw: this.facilities.getPowerKw(),
    };
  }

  /**
   * What the polity above allocates this district, in kW, or `null` for none.
   *
   * A national input (§4), and the only one that exists. Nothing inside the
   * simulation calls it: there is no polity yet, so the district runs on its
   * own interconnect and a scripted governor is whatever calls this.
   */
  setPowerAllocation(kw: number | null): void {
    this.power.setAllocation(kw);
  }

  // --- The mains -----------------------------------------------------------

  /**
   * What drinks, as the mains is allowed to see it.
   *
   * The same discipline as `readPower()` and the same walk of the house list,
   * one cycle apart, reading it differently on purpose:
   *
   *   - *gardens* is every built building, because the district waters its
   *     landscaping whether anybody lives there or not;
   *   - *homes* is the **occupied** ones only, because a tap nobody turns draws
   *     nothing. The grid disagrees, and is right to: a meter is a meter.
   */
  private readWater(): WaterReading {
    const gardens: number[] = [];
    const premises: number[] = [];
    const homes: number[] = [];

    for (const house of this.houses.getHouses()) {
      if (!house.built) continue;
      gardens.push(house.id);
      if (house.occupant === 'corporation') premises.push(house.id);
      else if (!house.vacant) homes.push(house.id);
    }

    return { gardens, premises, homes, facilityM3: this.facilities.getWaterM3() };
  }

  /**
   * What the polity above allocates this district, in m³ per cycle, or `null`.
   *
   * `setPowerAllocation`'s sibling, with the same standing: a national input
   * that nothing inside the simulation sets, waiting for a governor's
   * `set_water_priority`.
   */
  setWaterAllocation(m3: number | null): void {
    this.water.setAllocation(m3);
  }

  /**
   * This season's rainfall, as a fraction of a normal year.
   *
   * The one input in the district that comes from physics rather than from
   * somebody's decision (§4, settlement stage 1). It scales what the district's
   * own source yields and leaves an allocation alone — see `WaterManager`.
   *
   * §9's *Drought of Season 41* is `setRainfall(0.4)`, and at the shipped mains
   * that is a district whose lawns go brown and whose taps do not.
   */
  setRainfall(factor: number): void {
    this.water.setRainfall(factor);
  }

  // --- The labour market ----------------------------------------------------

  /**
   * The two questions a family asks before setting off for work.
   *
   * A frozen adapter rather than the manager, and rebuilt never: `HouseManager`
   * is handed this object up to sixty times a second, and it must not be able to
   * reach a facility, a wage or a ledger through it.
   */
  private readonly commuting = {
    jobOf: (familyId: number) => this.labour.jobOf(familyId),
    isWithdrawn: (facilityId: number) => this.labour.isWithdrawn(facilityId),
  };

  /**
   * The two questions asked before anybody moves into a vacant house.
   *
   * A frozen adapter for the same reason `commuting` is one: `HouseManager` holds
   * it for the life of the district and must not be able to reach a flow, a score
   * or a ledger through it.
   *
   * `admits` is the whole of the arrival half of §10, and it is deliberately
   * about the *house* rather than the district. Nobody rents a home with no power
   * and no water; everything else about a district being a bad place to live
   * reaches a newcomer through `newcomerScale`, as a smaller purse rather than as
   * a refusal. That split is what makes a failing district lose population
   * instead of churning through households — in a total blackout every vacancy is
   * refused, so the houses the departures empty stay empty.
   */
  private readonly housing = {
    admits: (houseId: number) => !this.power.isShed(houseId) && !this.water.isDry(houseId),
    newcomerScale: () => this.welfare.newcomerScale(),
    refused: () => this.welfare.recordRefusedArrival(),
  };

  /**
   * The one question asked before the district builds on a cell.
   *
   * A frozen adapter for the same reason `commuting` and `housing` are, and with the
   * narrowest surface of the three: `HouseManager` may find out that a cell is
   * spoken for and may not find out anything else about the district's works — not
   * what they cost, not how many there are, not that they exist as a category.
   */
  private readonly land = {
    isDeeded: (x: number, y: number) => this.mercy.isDeeded(x, y),
  };

  /**
   * Who is hiring and who could be hired, as the labour market is allowed to see
   * it.
   *
   * The third reading of the same district in the same cycle, after `readPower`
   * and `readWater` and on the same terms: a bag of ids and coordinates rather
   * than a district, so that nothing here can grow an opinion about traffic,
   * bushes, or what is under the ground it is siting jobs on.
   *
   * Every family is a candidate, including the ones whose houses are still going
   * up. A family exists from the moment its home is started, it has cash and bills
   * from that moment, and a rule that made employment wait for the roof would be a
   * rule this build has no way to justify — §10's housing and quality-of-life
   * terms are where that judgement eventually lives.
   */
  private readLabour(): LabourReading {
    // Open facilities only. A building site advertises nothing: §7 gives a forced
    // siting *"no local-hire agreement"* and gives an unfinished one no posts at
    // all, so the eight families are hired on the season the machine hall opens
    // and not one cycle before.
    const postings = this.facilities.getFacilities().filter((f) => f.openedTick !== null)
      .map((facility) => {
      const gate = this.facilities.gateOf(facility.id)!;
      return {
        facilityId: facility.id,
        staff: this.facilities.staffFor(facility.type),
        x: gate.x,
        y: gate.y,
      };
    });

    const candidates: LabourReading['candidates'] = [];
    // Only families with a home still standing: where somebody lives is the only
    // input to who gets hired, so a family the market cannot locate cannot be
    // ranked against one it can.
    for (const family of this.houses.getAllFamilies()) {
      const home = this.houses.getHouseById(family.homeHouseId);
      if (!home) continue;
      candidates.push({
        familyId: family.id,
        x: home.gridX,
        y: home.gridY,
        // Where they are *now*, which is what the payroll pays against. The
        // market cannot ask a family anything, so presence travels in the
        // reading beside the address.
        atFacility: family.atFacilityId,
      });
    }

    return {
      cols: this.grid.getCols(),
      rows: this.grid.getRows(),
      postings,
      candidates,
    };
  }

  /**
   * Pay this cycle's wages into the families that earned them.
   *
   * Here rather than in `LabourManager` because family cash lives in
   * `HouseManager` and the market cannot see one — the same split as the season
   * readout, in the other direction. The market says who is owed what; this
   * object is the only thing that can find them.
   *
   * The money comes from nowhere, and that is the honest state of a district with
   * no polity above it: a wage is federal spending, the account it leaves is the
   * polity's, and there is no polity. What exists instead is the measurement —
   * `LabourManager`'s `wagesPaid` — so that when §8's fiscal federalism arrives
   * there is a number for it to bill against. Third time in four ADRs: the ledger
   * before the mechanic that reads it.
   *
   * Wages land *after* the budget cycle that has just settled, so a family spends
   * this cycle's wage in the next one. That is a week's lag at the district's own
   * clock and it is not modelled as one — it is simply where in the tick the
   * payment falls, and saying so is cheaper than pretending it was chosen.
   */
  private payWages(): void {
    const payroll = this.labour.payroll();
    if (payroll.length === 0) return;
    for (const [familyId, amount] of payroll) {
      this.houses.addFamilyCash(familyId, amount);
    }
    this.labour.recordPayroll(payroll);
  }

  /**
   * Pay the estate's rent into the district's treasury.
   *
   * §6's *"Land lease | District government pool"*, at §7's statutory rate,
   * pro rata per budget cycle (`FacilityManager.getLeasePerCycle`). Here rather
   * than in either manager for the same reason wages are: the estate knows what
   * it owes and the treasury holds the money, and neither can see the other.
   *
   * Unconditional on everything. A curtailed facility pays it, a struck one pays
   * it, and a facility nobody can drive to pays it — because it is rent on ground
   * that is sealed in all three cases. That is the one line in this build that a
   * mayor can rely on, and it is the only thing the district is ever *given*.
   */
  private payLease(): void {
    const lease = this.facilities.getLeasePerCycle();
    if (lease <= 0) return;
    this.economy.receiveLease(lease);
    this.facilities.recordLease(lease);
  }

  // --- The market -----------------------------------------------------------

  /**
   * Place the estate's standing orders with the shops nearest the slab.
   *
   * §6's fourth revenue line — *"Local procurement | 1–2 host-district
   * corporations get standing orders | Fraction of opex"* — and the only channel
   * in the whole design through which federal money reaches a district's **tax
   * base** rather than its rent roll. A lease is rent and a wage is a household;
   * this is turnover, so the district's cut of it is `TAX_RATE`, collected next
   * cycle by machinery that predates every word of `DESIGN-GOVERNORS.md` and
   * knows nothing about facilities.
   *
   * **Which corporations is geography.** The built corporations nearest a
   * facility gate, by Manhattan distance, ties broken by house id, up to
   * `PROCUREMENT_SUPPLIERS` of them, sharing the cycle's orders equally. Nothing
   * negotiates and nothing is awarded: a data centre buys from the shops it can
   * see, and a mayor who zones commerce beside the slab catches the orders while
   * one who zones it across the map does not. §10 already gives the mayor
   * `prepare_site` for *"courting facilities"*; this is what courting one is
   * worth after it lands.
   *
   * The money is paid whether or not anybody is there to take it. A district with
   * a facility and no built corporation procures nothing — the estate's spending
   * leaves the polity's account either way and simply does not arrive here, which
   * is the honest state of a company town with no town.
   */
  private payProcurement(trade: CycleTrade): void {
    if (trade.procurement <= 0) return;
    const suppliers = this.procurementSuppliers();
    if (suppliers.length === 0) return;

    const share = trade.procurement / suppliers.length;
    for (const houseId of suppliers) this.houses.addHouseRevenue(houseId, share);
    // Booked after it has landed, not when it was owed. See `recordProcurement`.
    this.market.recordProcurement(trade.procurement);
  }

  /**
   * The built corporations holding the estate's standing orders, by house id.
   *
   * Recomputed every cycle rather than held, because a corporation can be built,
   * demolished or paved over between two of them, and a standing order to a shop
   * that is no longer there would be money leaving the polity and arriving
   * nowhere. The walk is over `getHouses()` in insertion order and the sort is
   * explicit on both keys (`docs/determinism.md` rule 4).
   */
  private procurementSuppliers(): number[] {
    const gates = this.facilities
      .getFacilities()
      .map((facility) => facilityGate(facility));
    if (gates.length === 0) return [];

    const candidates: Array<{ id: number; distance: number }> = [];
    for (const house of this.houses.getHouses()) {
      if (!house.built || house.occupant !== 'corporation') continue;
      let distance = Infinity;
      for (const gate of gates) {
        const d = Math.abs(house.gridX - gate.x) + Math.abs(house.gridY - gate.y);
        if (d < distance) distance = d;
      }
      candidates.push({ id: house.id, distance });
    }

    candidates.sort((a, b) => (a.distance - b.distance) || (a.id - b.id));
    const wanted = Math.max(0, Math.floor(this.market.getConfig().PROCUREMENT_SUPPLIERS));
    return candidates.slice(0, wanted).map((c) => c.id);
  }

  /**
   * Post a world price to this district.
   *
   * A national input in exactly `setRainfall`'s standing: nothing inside the
   * simulation calls it, and whatever does is standing in for §5's market, which
   * is Phase 3's. A posted bond yield takes effect immediately — it is what the
   * district borrows at from the next accrual, not from the next season — because
   * the alternative is a rate that is true in a Chronicle and false in a treasury.
   */
  postMarket(prices: { computePrice?: number; goodsPrice?: number; bondYield?: number }): boolean {
    const moved = this.market.post(prices);
    if (moved) this.applyBondYield();
    return moved;
  }

  /**
   * Hand the market's posted yield to the treasury.
   *
   * §8: debt interest *"stops being a local slider and becomes a World Market
   * rate"*. This is the sentence, and it is one assignment: `INTEREST_RATE` is no
   * longer a district's own number, it is a copy of the market's, refreshed at
   * every point either could have moved — a posting, a config edit, a restore.
   *
   * Not called per tick, deliberately. A per-tick sync would be a comparison
   * sixty times a second to catch a value that only three code paths can change,
   * and one of those three is a settings panel.
   */
  private applyBondYield(): void {
    this.economy.updateConfig({ INTEREST_RATE: this.market.getBondYield() });
  }

  // --- The corridor ---------------------------------------------------------

  /**
   * Settle a cycle's freight: collect the tolls, and wear the roads.
   *
   * One call, two effects, on exactly the same legs — which is the point. §6 gives
   * the host district a toll *"per truck-leg"* and §5 gives it *"road wear"* from
   * the same trucks, and a build that shipped one without the other would be
   * shipping a subsidy or a tax rather than a corridor. Whether the trade is worth
   * taking is `TOLL_PER_LEG` against `WEAR_PER_LEG` and against what a mayor pays
   * to keep a road at a hundred, and nothing here computes that for anybody.
   *
   * The wear lands *after* `EconomyManager.update` has already spent this cycle's
   * maintenance budget, so a district that repaired its roads and was then crossed
   * shows the crossing. The tolls land after the cycle too, like the rent, so they
   * are the *next* cycle's receipts.
   */
  private payTolls(): void {
    // The polity's own lorries first, because they are the ones with nothing on
    // the other side of the line. See `wearForConvoys`.
    this.wearForConvoys();
    const haulage = this.freight.haulage();
    if (haulage.legs <= 0) return;
    this.economy.receiveTolls(haulage.tolls);
    this.economy.wearRoads(haulage.wear);
    this.freight.recordHaulage(haulage);
  }

  /**
   * Put this cycle's national freight on the district's roads.
   *
   * The corridor decides how many legs are due and between which interchanges;
   * traffic decides whether there is room at the gate. Neither can see the other,
   * so this is where they meet — the same split as the estate's rent and the
   * market's payroll, and the third time in this class that a manager hands over a
   * list and finds out how much of it took.
   *
   * A leg with nowhere to appear is lapsed rather than queued. A corridor whose
   * trucks waited for a parking space to clear would be a corridor whose freight
   * volume depended on how many people happened to be at the shops, and §5 wants
   * capacity to be *"routes × road quality × congestion"* measured on the road
   * rather than inferred at the gate.
   */
  private dispatchFreight(): void {
    for (const leg of this.freight.due()) {
      if (this.cars.dispatchTruck(leg.id, leg.from, leg.to)) this.freight.dispatched();
      else this.freight.lapsed();
    }
  }

  /**
   * Book how the trucks that finished this tick finished.
   *
   * Drained every tick rather than every cycle, so the out-tray is empty at every
   * snapshot boundary and does not have to be saved. What is *paid* on the
   * completions is a cycle's business (`payTolls`); what is counted is immediate,
   * because a leg that ended is a fact and not a transaction.
   */
  private collectFreight(): void {
    for (const outcome of this.cars.takeFreightOutcomes()) {
      // Two kinds of lorry finish on these roads and they are counted in two
      // different ledgers, because they are two different transactions: a
      // crossing is a service the district sold the world and earns a toll, and a
      // delivery is a load somebody dropped at a slab the district never agreed
      // to host. The truck does not know the difference; its manifest does.
      if (outcome.facilityId !== null) {
        this.facilities.convoyOutcome(outcome.facilityId, outcome.arrived);
      } else {
        this.freight.outcome(outcome.arrived);
      }
    }
  }

  // --- The building site ----------------------------------------------------

  /**
   * Put this cycle's construction convoys on the district's roads.
   *
   * §7's `CONSTRUCTION` phase — *"goods convoys arrive each season or progress
   * stalls"* — and the third object in this class that hands a manager a list and
   * finds out how much of it took. The estate says what its building sites are
   * owed and where; this method is the only thing that can see a way into the
   * district, and it is where the goods are bought.
   *
   * **Where a lorry enters** is the whole of the mayor's leverage over a seizure,
   * and it is two rules with a precedence between them:
   *
   *   - **an interchange, if the district has built any** — the one nearest the
   *     site. §5 defines an interchange as *"where national freight enters and
   *     leaves the map"*, so a district that has built one has told the world
   *     where its lorries come in, and the world believes it.
   *   - **otherwise, wherever the pavement comes closest to the edge of the map**,
   *     ties broken by which of those is nearest the site and then by grid order.
   *     The goods come from off-map either way; with no gate to enter by, they
   *     improvise at the end of the road.
   *
   * So the $600 an interchange costs buys a mayor the *route* the construction
   * traffic takes — and a second one beside the slab is the shortest route there
   * is, which is fewer lorries on the roads at once, fewer that give up, and fewer
   * things under their wheels for two seasons. §7 sells the same thing as
   * `prepare_site` and pays for it in construction *seasons*; this build has no
   * Siting Protocol to charge that through, so what readiness buys here is
   * geometry.
   *
   * The precedence has a trap in it and it is deliberate: a district whose only
   * interchange is in the far corner routes every lorry through the far corner,
   * and would have done better with none. That is what declaring a freight gate
   * means, it is fixed by declaring a second one, and nothing anywhere says so.
   *
   * A district with **no interchange and no pavement at all** has no way in: every
   * convoy lapses, nothing is bought, and construction stalls until somebody
   * builds a road. Which is Law 6's shape — force stays available, and what it
   * costs is arithmetic somebody else controls.
   */
  private dispatchConvoys(): void {
    const orders = this.facilities.convoysDue();
    if (orders.length === 0) return;

    // One walk of the grid per building site per cycle at most, rather than one
    // per lorry: the entry point is a fact about the map and the map does not
    // change between two orders in the same list.
    const entries = new Map<number, GridCoord | null>();
    for (const order of orders) {
      let entry = entries.get(order.facilityId);
      if (entry === undefined) {
        entry = this.convoyEntry(order.gate);
        entries.set(order.facilityId, entry);
      }
      if (!entry) {
        this.facilities.convoyLapsed(order.facilityId);
        continue;
      }
      const legId = this.freight.claimLegId();
      if (this.cars.dispatchTruck(legId, entry, order.gate, order.facilityId)) {
        // Bought when the lorry sets off, so a load that never arrives is a load
        // the polity paid for. See `MarketManager.buyGoods`.
        this.market.buyGoods(order.goods);
        this.facilities.convoyDispatched(order.facilityId);
      } else {
        this.facilities.convoyLapsed(order.facilityId);
      }
    }
  }

  /**
   * Where a lorry bound for this gate enters the district, or `null` for nowhere.
   *
   * Walked rather than cached: an interchange is built and pavement reaches
   * further out between one cycle and the next, and an entry point held from last
   * season would be a lorry appearing on grass. The walk is the interchange list,
   * or — for a district that has built none — every cell of the map, once per
   * building site per budget cycle, and nothing at all on a district with no
   * building site.
   *
   * See `dispatchConvoys` for what the two branches mean.
   */
  private convoyEntry(gate: GridCoord): GridCoord | null {
    let best: GridCoord | null = null;
    let bestKey = Number.POSITIVE_INFINITY;
    let bestDistance = Number.POSITIVE_INFINITY;

    const interchanges = this.freight.getInterchanges();
    if (interchanges.length > 0) {
      for (const structure of interchanges) {
        for (const cell of interchangeCells(structure)) {
          const distance = this.wrappedManhattan(cell.x, cell.y, gate.x, gate.y);
          if (distance < bestDistance) {
            best = { x: cell.x, y: cell.y };
            bestDistance = distance;
          }
        }
      }
      return best;
    }

    const cols = this.grid.getCols();
    const rows = this.grid.getRows();
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (!this.isRoad(x, y)) continue;
        // How far this pavement is from the outside world. The lorries come in
        // wherever that is smallest — the end of the road — and the tie between
        // two ends is broken by which is nearer the site, then by grid order.
        const edge = Math.min(x, cols - 1 - x, y, rows - 1 - y);
        const distance = this.wrappedManhattan(x, y, gate.x, gate.y);
        if (edge < bestKey || (edge === bestKey && distance < bestDistance)) {
          best = { x, y };
          bestKey = edge;
          bestDistance = distance;
        }
      }
    }

    return best;
  }

  /** Whether a cell is pavement a lorry can appear on. */
  private isRoad(x: number, y: number): boolean {
    const state = this.grid.getCell(x, y)?.state;
    return (
      state === CellState.POURED ||
      state === CellState.CURED ||
      state === CellState.PAINTED
    );
  }

  /** Manhattan distance across the wrap, which is how far a vehicle drives. */
  private wrappedManhattan(ax: number, ay: number, bx: number, by: number): number {
    const cols = this.grid.getCols();
    const rows = this.grid.getRows();
    const dx = Math.abs(ax - bx);
    const dy = Math.abs(ay - by);
    return Math.min(dx, cols - dx) + Math.min(dy, rows - dy);
  }

  /**
   * Wear the roads for the lorries that delivered since the last cycle.
   *
   * The same rate a crossing wears (`WEAR_PER_LEG`) and **no toll at all**, which
   * is the sharpest line in this build's arithmetic of force. §6 pays a district a
   * toll *"per truck-leg"* for freight it carries *for the world*; a convoy
   * bringing a federal building site its concrete is not carrying anything for
   * anybody here. The road is worn the same, the ants under it die the same, and
   * the district's half of the ledger is empty.
   *
   * Booked in the same breath as the toll, and after `EconomyManager.update` has
   * spent this cycle's maintenance — so a district that repaired its roads and was
   * then delivered to shows the delivery.
   */
  private wearForConvoys(): void {
    const legs = this.facilities.convoyWear();
    if (legs <= 0) return;
    this.economy.wearRoads(legs * this.freight.getConfig().WEAR_PER_LEG);
    this.facilities.recordConvoyWear(legs);
  }

  /**
   * Advance the building sites by a season, and say so when one opens.
   *
   * §3's seventh settlement stage, which has been empty since ADR-012 for the
   * reason `SeasonManager` gives — *"a build timer with nothing being delivered
   * into it would be a countdown, which is the shape of an authored delta"*. There
   * is something being delivered into it now.
   *
   * The stage is the second in the pipeline with work in it, after the tithe, and
   * it is here rather than in `SeasonManager` on exactly the tithe's terms: a
   * settler that could open a facility could reach a colony (Law 7).
   */
  private settleConstruction(): void {
    for (const facility of this.facilities.advanceConstruction(this.tickCount)) {
      // The one line a mayor gets, in the district's own voice and about the only
      // part of it a district can see: the lorries stop. Nothing says whose
      // building it is, what it is for, or that eight of the town's families are
      // about to be offered jobs in it.
      this.houses.addNews(
        `${FACILITY_SPECS[facility.type].label} completed on the federal parcel`,
        'economy'
      );
    }
  }

  /**
   * How much national freight crosses this district, in truck-legs per budget
   * cycle.
   *
   * `setPowerAllocation`'s third sibling, with the same standing: a national input
   * (§4) that nothing inside the simulation sets, because there is no polity. A
   * district with two interchanges and a dial at zero has built a corridor nobody
   * is using, which is a perfectly ordinary thing for a district to have done.
   */
  setFreight(legsPerCycle: number | null): void {
    this.freight.setOffered(legsPerCycle);
  }

  // --- The households -------------------------------------------------------

  /**
   * How each household is doing, as the measure is allowed to see it.
   *
   * The fourth reading of the same district in the same cycle, on the same terms
   * as the three above it: ids and numbers, assembled here because this is the
   * one object that can ask the grid whether a house is dark, the mains whether
   * its taps run, the market whether anybody in it has a job, and the ground what
   * is left of the meadow around it.
   *
   * Two resolutions happen here rather than in the measure, because both need to
   * know what a house *is*:
   *
   *   - **A stalled building site is not a family in the dark.** The grid sheds
   *     sites first and homes last, and a family exists from the moment its home
   *     is started — so `dark` is `shed && built`, and a household waiting for a
   *     roof is not counted as having spent the cycle in one that had no lights.
   *   - **A household with no home is not measured at all.** The same rule the
   *     labour market uses: a family the district cannot locate cannot be scored
   *     against one it can.
   */
  private readWelfare(): WelfareReading {
    const radius = this.welfare.getConfig().GREEN_RADIUS;
    const households: WelfareReading['households'] = [];

    for (const family of this.houses.getAllFamilies()) {
      const home = this.houses.getHouseById(family.homeHouseId);
      if (!home) continue;
      const { green, greenOf } = this.countGreen(home.gridX, home.gridY, radius);
      households.push({
        familyId: family.id,
        cash: family.cash,
        employed: this.labour.jobOf(family.id) !== null,
        // Shed, built or not. ADR-018 excluded stalled construction sites on the
        // grounds that "a stalled building site is not a family in the dark",
        // which is true of the *building* — the grid sheds sites first precisely
        // because nobody is hurt — and false of a family that already lives at
        // one. Measured: a district allocated no power emptied to a single
        // household camped at a site that would never be finished, and because
        // that household was "not dark" the district's mean score read 0.94 with
        // nobody left in it. See ADR-019 D119.
        dark: this.power.isShed(home.id),
        dry: this.water.isDry(home.id),
        green,
        greenOf,
        // The reservoir, which lives on the family. See `HouseholdReading.spent`.
        spent: family.hardshipCycles,
      });
    }

    // One number for the whole district, because the infrastructure budget buys
    // one road network. `getRoadQuality()` is 0–100 and this class is the only
    // thing that knows the measure wants a fraction.
    return { households, roads: this.economy.getRoadQuality() / 100 };
  }

  /**
   * Meadow within `radius` cells of a point, and how many cells that was.
   *
   * §10's *"green cells within radius 3"*, in the Chebyshev sense — a square,
   * because the isometric grid's neighbours are its eight adjacent cells and a
   * Euclidean disc would be a shape nothing else in this game uses.
   *
   * The window is **clipped at the map's edge** rather than wrapped, and the
   * denominator travels with the count so the measure can tell the difference. A
   * household on the last row is judged on the ground that exists beside it,
   * which is the answer that needs no rule; measuring across the wrap, the way
   * the labour market measures a commute, would say a house at the bottom of the
   * map has a meadow at the top of it, and nobody can walk there.
   */
  private countGreen(x: number, y: number, radius: number): { green: number; greenOf: number } {
    let green = 0;
    let greenOf = 0;
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const cell = this.grid.getCell(x + dx, y + dy);
        if (!cell) continue;
        greenOf++;
        if (cell.state === CellState.NATURAL) green++;
      }
    }
    return { green, greenOf };
  }

  /**
   * Score the households, and act on the ones that are leaving.
   *
   * Here rather than in `WelfareManager` for the reason the payroll is: a
   * departure is a house going vacant, a shareholding being unwound and a line in
   * the ticker, and none of those is something a measure should be able to reach.
   * The measure says who and why; this object is the only thing that can find
   * them.
   *
   * §10 gives a departing family three destinations — a sibling district with
   * housing, a treaty-open foreign polity, or off-world. Two of those need a
   * world and the third needs nothing, so every family who leaves Parkington
   * today is simply gone, which is what has happened to every family who ever
   * left it. The Chronicle counts them by cause, which is the half of §10's
   * *"the one poll that can't be spun"* a district can keep on its own.
   */
  private settleWelfare(): void {
    const verdict = this.welfare.settle(this.readWelfare());

    for (const [familyId, spent] of verdict.spent) {
      this.houses.setHardshipCycles(familyId, spent);
    }

    for (const departure of verdict.departures) {
      this.houses.removeFamilyById(departure.familyId, departureReason(departure));
      this.welfare.recordDeparture(departure.cause);
      this.welfare.release(departure.familyId);
    }

    // And then somebody moves in, or does not.
    //
    // This ran inside the treasury's cycle until ADR-019, one house per cycle and
    // unconditionally. It is here because the gate needs the grid, the mains and
    // the score, and the treasury can see none of the three — the same reason the
    // payroll and the rent are paid here. After the departures rather than before,
    // so a house vacated this cycle is on offer this cycle: the one-cycle delay
    // ADR-018 D111 shipped was a consequence of the old ordering rather than a
    // decision, and with the gate in place it is not worth keeping.
    this.houses.tryFillVacantHome(this.housing);
  }

  // --- The season ----------------------------------------------------------

  /** The season this district is living through. 1 for its first. */
  getSeason(): number {
    return seasonOf(this.economy.getCyclesSettled());
  }

  /** The district's own Chronicle, oldest first. */
  getChronicle(): readonly SeasonRecord[] {
    return this.seasons.records();
  }

  /**
   * The district, as the season settlement is allowed to see it.
   *
   * Built here because this is the object that has the managers; handed over as
   * a bag of numbers because the settler must not be able to reach anything
   * else (Law 7, `SeasonManager`'s header). Taken once per season, which is what
   * makes the cell walks below affordable — per tick they would not be.
   */
  private readSeason(): SeasonReadout {
    const counts = this.grid.countCellsByState();
    const houses = this.houses.getHouses();
    const demand = this.power.getDemand();
    const ledger = this.power.getLedger();
    const water = this.water.getDemand();
    const mains = this.water.getLedger();
    const estate = this.facilities.getEstateLedger();
    const payroll = this.labour.getLedger();
    const staffing = this.labour.getStaffing();
    const lease = this.economy.getTotalLeaseCollected();
    const works = this.economy.getTotalWorksSpending();
    const bills = this.economy.getTotalUtilityBilled();
    const households = { ...this.welfare.getLedger(), meanQol: this.welfare.meanQol() };
    const mercy = this.mercy.getLedger();
    const account = this.fiscal.getLedger();
    const corridor = this.freight.getLedger();
    const world = this.market.getLedger();

    return {
      tick: this.tickCount,
      simTimeMs: this.simTimeMs,
      land: {
        paved: this.grid.countPavedCells(),
        natural: counts[CellState.NATURAL],
        // Standing bushes: natural ground only. Until ADR-015 this counted every
        // cell a bush had ever grown on, which is a meadow that cannot shrink.
        bush: this.grid.countStandingVegetation('bush'),
        fruit: this.foodSources.getTotalFood(),
      },
      town: {
        houses: houses.length,
        built: this.houses.getBuiltCount(),
        families: this.houses.getAllFamilies().length,
        corporations: houses.filter(h => h.occupant === 'corporation' && h.built).length,
        cars: this.cars.getCarCount(),
      },
      treasury: {
        pool: this.economy.getGovernmentPool(),
        roadQuality: this.economy.getRoadQuality(),
        taxCollected: this.economy.getTotalTaxCollected(),
        // Absent rather than zero in a district that has never had a tenant. The
        // key would otherwise appear in every Chronicle ever recorded here.
        ...(lease > 0 ? { leaseCollected: lease } : {}),
        ubiDistributed: this.economy.getTotalUBIDistributed(),
        dividendsDistributed: this.economy.getTotalDividendsDistributed(),
        businessGrantsPaid: this.economy.getTotalBusinessGrantsPaid(),
        infraSpending: this.economy.getTotalInfraSpending(),
        // Absent on the same terms as the rent above it, and by the same districts.
        ...(works > 0 ? { worksSpending: works } : {}),
        // And so is the utility bill, though by far fewer districts: everything
        // with a building on it draws something. Absent in an untouched meadow.
        ...(bills > 0 ? { utilityBilled: bills } : {}),
        interestPaid: this.economy.getTotalInterestPaid(),
      },
      power: {
        supplyKw: this.power.getSupplyKw(),
        demandKw: demand.total,
        shedKw: this.power.getShedKw(),
        darkBuildings: this.power.getShedCount(),
        meteredCycles: ledger.meteredCycles,
        shortfallCycles: ledger.shortfallCycles,
        deliveredKwCycles: ledger.deliveredKwCycles,
        shedKwCycles: ledger.shedKwCycles,
        darkBuildingCycles: ledger.darkHouseCycles,
      },
      water: {
        rainfall: this.water.getRainfall(),
        supplyM3: this.water.getSupplyM3(),
        demandM3: water.total,
        cutM3: this.water.getCutM3(),
        parchedGardens: this.water.getParchedCount(),
        dryBuildings: this.water.getDryCount(),
        meteredCycles: mains.meteredCycles,
        shortfallCycles: mains.shortfallCycles,
        deliveredM3Cycles: mains.deliveredM3Cycles,
        cutM3Cycles: mains.cutM3Cycles,
        parchedGardenCycles: mains.parchedGardenCycles,
        dryBuildingCycles: mains.dryBuildingCycles,
      },
      // Omitted by a district with nothing federal on it, which is every district
      // that has ever been recorded here. `SeasonFacilities` says why the key is
      // absent rather than a block of zeroes.
      facilities: this.facilities.count === 0 ? undefined : {
        count: this.facilities.count,
        operating: this.facilities.operatingCount,
        sealedCells: this.facilities.sealedCells,
        demandKw: demand.facilities,
        demandM3: water.facilities,
        uptime: this.facilities.getUptime().uptime,
        meteredCycles: estate.meteredCycles,
        curtailedCycles: estate.curtailedCycles,
        uptimeCycles: estate.uptimeCycles,
        computeUnits: estate.computeUnits,
        operatingCost: estate.operatingCost,
        leasePaid: estate.leasePaid,
        goodsDelivered: estate.goodsDelivered,
        convoysDispatched: estate.convoysDispatched,
        convoysLost: estate.convoysLost,
        convoysLapsed: estate.convoysLapsed,
      },
      // Omitted while nobody has quoted this district a price and nothing has
      // traded — which is every district recorded here, and is why §5's boundary
      // condition arrived without moving a hash on its own account. Note that a
      // *posting* alone writes the block: a governor who repriced compute and
      // then sited nothing has still done something to this district, and §16
      // should be able to say so.
      market: this.market.empty ? undefined : {
        computePrice: this.market.getComputePrice(),
        goodsPrice: this.market.getGoodsPrice(),
        bondYield: this.market.getBondYield(),
        computeSold: world.computeSold,
        salesRevenue: world.salesRevenue,
        operatingCost: world.operatingCost,
        procurementPaid: world.procurementPaid,
        goodsBought: world.goodsBought,
        capitalSpend: world.capitalSpend,
        tradedCycles: world.tradedCycles,
        postings: world.postings,
      },
      // Omitted on the same terms, and by the same districts: no facility means
      // no posts, and no posts means nobody has ever worked for anybody's polity.
      labour: this.facilities.count === 0 && !this.labour.employed ? undefined : {
        posts: this.facilities.staffRequired,
        hired: this.labour.hired,
        staffed: staffing.staffed,
        strikes: this.labour.strikes().length,
        meteredCycles: payroll.meteredCycles,
        staffedCycles: payroll.staffedCycles,
        vacancyCycles: payroll.vacancyCycles,
        shiftCycles: payroll.shiftCycles,
        withdrawnCycles: payroll.withdrawnCycles,
        wagesPaid: payroll.wagesPaid,
        wageCycles: payroll.wageCycles,
      },
      // Omitted by a district where every household has always been solvent, lit
      // and watered — which is every district anybody has recorded here, and is
      // why the Chronicle's shape has not changed. A record that carried a
      // quality-of-life block full of zeroes would be a district reporting on
      // people it has nothing to report about.
      welfare: !this.welfare.reported ? undefined : {
        strained: this.welfare.strainedCount(),
        qol: households.meanQol ?? 1,
        strainedCycles: households.strainedCycles,
        darkHouseholdCycles: households.darkHouseholdCycles,
        dryHouseholdCycles: households.dryHouseholdCycles,
        departures: households.departures,
        arrivalsRefused: households.arrivalsRefused,
        departuresBy: households.departuresBy,
      },
      // Omitted by a district whose mayor has never built anything that pays
      // nothing — which is every district in this repository, and is why ADR-020
      // moved no golden master either. The block is the mayor's only entry in a
      // public record they otherwise only appear in as a set of consequences.
      mercy: this.mercy.empty ? undefined : {
        covenantCells: this.mercy.deededCells,
        saplingCells: this.mercy.saplingCells,
        crossingCells: this.mercy.crossingCells,
        covenantsRegistered: mercy.covenantsRegistered,
        hedgerowsPlanted: mercy.hedgerowsPlanted,
        hedgerowsGrown: mercy.hedgerowsGrown,
        hedgerowsLost: mercy.hedgerowsLost,
        crossingsBuilt: mercy.crossingsBuilt,
        condemnedCells: mercy.condemnedCells,
        spent: mercy.spent,
      },
      // Omitted by a district nobody has billed, tithed or rationed — which is a
      // meadow, and only a meadow. Every other district in this repository now has
      // this block, because everything with a building on it draws power and
      // everything that draws power is charged for it.
      fiscal: this.fiscal.empty ? undefined : {
        titheRate: this.fiscal.getTitheRate(),
        owed: this.fiscal.getOwed(),
        powerBilled: account.powerBilled,
        waterBilled: account.waterBilled,
        tithePaid: account.tithePaid,
        grantsReceived: account.grantsReceived,
        worksCommitted: account.worksCommitted,
        infraUnpaid: account.infraUnpaid,
        billedCycles: account.billedCycles,
        constrainedCycles: account.constrainedCycles,
        levies: account.levies,
      },
      // Omitted by a district with no interchange on it and no freight offered to
      // it — which is every district in this repository, and is why ADR-022 moved
      // no golden master either. A corridor is the one thing in this record that
      // a mayor and a polity both had to agree to: the mayor built the structures
      // and somebody above decided to route anything over them.
      freight: !this.freight.active ? undefined : {
        interchanges: corridor.interchanges,
        offered: this.freight.getOffered(),
        spent: corridor.spent,
        legsOffered: corridor.legsOffered,
        legsDispatched: corridor.legsDispatched,
        legsCompleted: corridor.legsCompleted,
        legsAbandoned: corridor.legsAbandoned,
        legsLapsed: corridor.legsLapsed,
        tollsEarned: corridor.tollsEarned,
        roadWear: corridor.roadWear,
      },
    };
  }

  /**
   * The one thing a settlement changes outside the Chronicle: a line in the
   * ticker.
   *
   * A report, not a delta — it says what the district already was. It goes
   * through the same channel the treasury uses for "Roads deteriorating", and it
   * is the only trace of a season a player sees until the Ledger Dock arrives in
   * Phase 4. Both figures in it are counted, and the sentence never draws a
   * conclusion from them, which is the register Parkington has always used about
   * its own growth.
   */
  private reportSeason(record: SeasonRecord): void {
    this.houses.addNews(
      `Season ${record.season} closed — ${record.town.built} structures, ` +
      `${record.land.paved} cells developed`,
      'economy'
    );
  }

  // --- Configuration -------------------------------------------------------

  getSurfaceForagingConfig(colonyIndex: number): SurfaceForagingConfig {
    return this.surfaceForagingConfigs.get(colonyIndex) ?? DEFAULT_SURFACE_FORAGING;
  }

  updateSurfaceForagingConfig(colonyIndex: number, partial: Record<string, number>): void {
    const existing = this.surfaceForagingConfigs.get(colonyIndex) ?? { ...DEFAULT_SURFACE_FORAGING };
    Object.assign(existing, partial);
    this.surfaceForagingConfigs.set(colonyIndex, existing);
  }

  updateCarConfig(partial: Partial<CarManagerConfig>): void { this.cars.updateConfig(partial); }
  updateHouseConfig(partial: Partial<HouseManagerConfig>): void { this.houses.updateConfig(partial); }
  updateEconomyConfig(partial: Partial<EconomyConfig>): void { this.economy.updateConfig(partial); }
  updatePowerConfig(partial: Partial<PowerConfig>): void { this.power.updateConfig(partial); }
  updateWaterConfig(partial: Partial<WaterConfig>): void { this.water.updateConfig(partial); }
  updateFacilityConfig(partial: Partial<FacilityConfig>): void { this.facilities.updateConfig(partial); }
  updateLabourConfig(partial: Partial<LabourConfig>): void { this.labour.updateConfig(partial); }
  updateWelfareConfig(partial: Partial<WelfareConfig>): void { this.welfare.updateConfig(partial); }
  updateMercyConfig(partial: Partial<MercyConfig>): void { this.mercy.updateConfig(partial); }
  updateFiscalConfig(partial: Partial<FiscalConfig>): void { this.fiscal.updateConfig(partial); }
  updateMarketConfig(partial: Partial<MarketConfig>): void {
    this.market.updateConfig(partial);
    this.applyBondYield();
  }
  updateFreightConfig(partial: Partial<FreightConfig>): void { this.freight.updateConfig(partial); }
  updateFoodConfig(partial: Partial<FoodSourceConfig>): void { this.foodSources.updateConfig(partial); }
  updatePheromoneConfig(partial: Partial<PheromoneGridConfig>): void { this.pheromones.updateConfig(partial); }

  /**
   * Apply computed trait parameters to internal systems.
   */
  applyTraitParams(params: SurfaceTraitParams): void {
    // Update pheromone config from trait-derived values
    const pheromoneUpdates: Record<string, number> = {};
    /* eslint-disable-next-line no-restricted-syntax -- Rule 4 does not bite: every
       key names a different field and is written exactly once, so the result is the
       same bag whatever order it is filled in. A named order is not available
       either — these keys come from a Charter, not from an enum this package can
       see. */
    for (const [key, value] of Object.entries(params.configParams)) {
      if (key.startsWith('parkington.pheromones.')) {
        const field = key.split('.').pop()!;
        pheromoneUpdates[field] = value;
      }
    }
    if (Object.keys(pheromoneUpdates).length > 0) {
      this.pheromones.updateConfig(pheromoneUpdates);
    }

    // Blend weights are a shared reference the ant controller reads each tick.
    Object.assign(this.blendWeights, params.blendParams);

    this.glandCapacity = params.glandCapacity;
    this.glandCostPerDeposit = params.glandCostPerDeposit;

    const tunables = this.surfaceAnts.tunables;
    tunables.pheromoneDepositInterval = params.depositInterval;
    tunables.wallFollowDuration = params.wallFollowDuration;
    tunables.glandCapacity = params.glandCapacity;
    tunables.glandCostPerDeposit = params.glandCostPerDeposit;
  }

  // --- State ---------------------------------------------------------------

  /**
   * A canonical, order-stable view of everything the surface has decided.
   *
   * Feed to `hashState()` to compare two runs. Deliberately excludes view state
   * — pan, zoom, hover, selection, the ants' screen positions — because the
   * mayor looking somewhere else must not change the ledger, and excludes the
   * terrain cells' cosmetic properties, which are a pure function of position.
   *
   * Like the colony's, this is the ancestor of `DistrictSnapshot` and is a
   * read-only view rather than a restorable one.
   */
  canonicalState(): Record<string, Canonical> {
    const cells: Canonical[] = [];
    for (const cell of this.grid.getAllCells()) {
      // Only cells that have been touched: the rest are a function of position
      // and would be 1,200 identical entries in every hash.
      if (cell.state === CellState.NATURAL) continue;
      cells.push({
        x: cell.x, y: cell.y, state: cell.state,
        concreteLevel: cell.concreteLevel, cureProgress: cell.cureProgress,
      });
    }


    const state: Record<string, Canonical> = {
      tick: this.tickCount,
      simTimeMs: this.simTimeMs,
      pheromoneUpdateTimer: this.pheromoneUpdateTimer,
      portalCells: this.portalCells.map(c => `${c.x},${c.y}`),
      cells,
      construction: this.construction.canonicalState(),
      houses: this.houses.canonicalState(),
      economy: this.economy.canonicalState(),
      cars: this.cars.canonicalState(),
      food: this.foodSources.canonicalState(),
      pheromones: this.pheromones.canonicalState(),
      surfaceAnts: this.surfaceAnts.canonicalState(),
      // The district generator's own position, on top of each fork's. Two
      // surfaces that agree on every house but disagree here will diverge as
      // soon as either forks again.
      rng: this.rng.save() as unknown as Canonical,
    };

    // The bushes somebody planted. Not in `cells` above, because a hedgerow leaves
    // its cell `NATURAL` and that loop skips those on the grounds that they are a
    // pure function of position — which is true of every cell in the district
    // except these. Omitted while there are none, like every key below it: two
    // districts that agree on every figure and differ by one bush are two
    // different meadows, and a colony finds out before anybody else does.
    const planted = this.grid.plantedCells();
    if (planted.length > 0) state.planted = planted;

    // The grid connection, once a cycle has been metered. Omitted before that
    // for the same reason as the two keys below: a meter that has never read
    // anything and no meter are the same world.
    const power = this.power.canonicalState();
    if (power !== undefined) state.power = power;

    // The mains, on the same terms. Its guard has one clause more than the
    // grid's: a district told it is in a drought has state before it has metered
    // anything, because the weather is an input and not a reading.
    const water = this.water.canonicalState();
    if (water !== undefined) state.water = water;

    // The federal estate, once there is one. The footprints are in it as well as
    // the ledgers: two districts that agree on every figure and disagree about
    // where the slab is are two different maps, and they diverge as soon as
    // anybody tries to build on the difference.
    const facilities = this.facilities.canonicalState();
    if (facilities !== undefined) state.facilities = facilities;

    // The labour market, once anybody has been hired or anything posted. Omitted
    // on the same terms and for the same reason as the three above it: a district
    // where nobody has ever held a federal job is a district in which this class
    // did not happen.
    const labour = this.labour.canonicalState();
    if (labour !== undefined) state.labour = labour;

    // The households, once one of them has had something go wrong. Omitted on
    // the same terms as everything above it and with the most at stake: every
    // family in every recorded run of this district is solvent, lit and watered,
    // so a measure that wrote a bag of zeroes for each of them would have
    // re-recorded eleven golden masters to say that nothing happened.
    const welfare = this.welfare.canonicalState();
    if (welfare !== undefined) state.welfare = welfare;

    // The district's own works, once the mayor has bought one. Omitted on the same
    // terms as everything above it, and the omission is doing more here than
    // elsewhere: the covenants have to be in the hash because a deeded cell is
    // `NATURAL` and therefore invisible to the cell list, so this is the only
    // record anywhere in the state of which ground is spoken for.
    const mercy = this.mercy.canonicalState();
    if (mercy !== undefined) state.mercy = mercy;

    // The account with the polity, once anything has been billed, levied or
    // rationed. Omitted on the same terms as everything above it, and here the
    // omission holds for exactly one kind of district — an untouched meadow, which
    // is one of the four surface golden masters and the only one this block does
    // not appear in.
    const fiscal = this.fiscal.canonicalState();
    if (fiscal !== undefined) state.fiscal = fiscal;

    // The world's prices, once one has moved or anything has traded. Omitted by
    // every district in this repository — a market nobody has quoted and no
    // market are the same market — which is what let §5's boundary condition
    // arrive without re-recording a hash on its own account.
    const market = this.market.canonicalState();
    if (market !== undefined) state.market = market;

    // The corridor, once the mayor has built an interchange or somebody above has
    // offered a leg. Omitted on the same terms as everything above it, and by every
    // district in this repository — which is what let a class with trucks in it
    // arrive without re-recording a single golden master.
    const freight = this.freight.canonicalState();
    if (freight !== undefined) state.freight = freight;

    // The Chronicle, once there is one. Omitted while it is empty, for the
    // reason `SeasonManager.canonicalState()` gives, and which is the same
    // reason the pending-command key below is omitted while the queue is empty.
    const chronicle = this.seasons.canonicalState();
    if (chronicle !== undefined) state.season = chronicle;

    // Accepted but unapplied commands are state for the same reason the
    // generator's position is: two worlds that agree on every house and differ
    // here have already diverged, they just have not noticed yet.
    //
    // The key is omitted when the queue is empty rather than written as `[]`.
    // An empty queue and no queue are the same world, which is exactly what
    // `canonicalize` means by not distinguishing absent from undefined — and it
    // is what let the queue arrive without moving a single recorded hash.
    //
    // `appliedThrough` is deliberately not hashed: the sim drains every tick, so
    // it is always `tick - 1` and would only restate what `tick` already says.
    // `tests/verbs.test.ts` asserts that invariant rather than assuming it.
    const pending = this.commands.pending();
    if (pending.length > 0) {
      state.pendingCommands = pending.map(c => ({
        tick: c.tick,
        actor: c.actor,
        seq: c.seq,
        verb: c.verb,
        args: (c.args ?? null) as Canonical,
      }));
    }

    return state;
  }

  /**
   * Everything the surface is, in a form it can be rebuilt from.
   *
   * Distinct from `canonicalState()`, and the difference is the point of both.
   * `canonicalState()` answers *did two runs agree* and so excludes anything that
   * is not a decision — a car's colour, a house's roof. This answers *can this
   * world be continued* and so includes them, because a reload that repainted the
   * street would be a reload somebody noticed.
   *
   * Neither contains the other, which is why the round-trip is checked three
   * ways: the hash must survive it (state the hash names), the snapshot must be
   * idempotent (state the snapshot names), and a restored world must run on
   * identically for thousands of ticks (state neither of them names, which is the
   * only test that can catch a field nobody thought of).
   */
  snapshot(): SurfaceSnapshot {
    return {
      tick: this.tickCount,
      simTimeMs: this.simTimeMs,
      pheromoneUpdateTimer: this.pheromoneUpdateTimer,
      portalCells: this.portalCells.map(c => ({ ...c })),
      rng: this.rng.save(),
      commands: this.commands.save(),
      terrain: this.grid.save(),
      construction: this.construction.save(),
      houses: this.houses.save(),
      economy: this.economy.save(),
      cars: this.cars.save(),
      food: this.foodSources.save(),
      pheromones: this.pheromones.save(),
      surfaceAnts: this.surfaceAnts.save(),
      power: this.power.save(),
      water: this.water.save(),
      facilities: this.facilities.save(),
      market: this.market.save(),
      labour: this.labour.save(),
      welfare: this.welfare.save(),
      mercy: this.mercy.save(),
      fiscal: this.fiscal.save(),
      freight: this.freight.save(),
      season: this.seasons.save(),
    };
  }

  /**
   * Become the world this snapshot describes.
   *
   * Total replacement, not a merge: whatever this surface was is gone, including
   * anything the snapshot has nothing to say about. That is what makes restoring
   * into a *used* world safe, and it is worth being strict about — a settlement
   * worker handed a season is far more likely to be recycling a district object
   * than to have built a clean one.
   *
   * The portals themselves are not in the snapshot. They are the wiring between
   * this surface and the colonies beneath it — a property of the district rather
   * than of the season — so a caller restores into a world whose portals are
   * already registered, and this method moves them to the entrances the snapshot
   * names.
   */
  restore(state: SurfaceSnapshot): void {
    this.tickCount = state.tick;
    this.simTimeMs = state.simTimeMs;
    this.pheromoneUpdateTimer = state.pheromoneUpdateTimer;

    // In place: the ant controller and the portal system hold this array by
    // reference, and handing them a new one would leave them reading the old map.
    this.portalCells.length = 0;
    for (const cell of state.portalCells) this.portalCells.push({ ...cell });

    this.rng.load(state.rng);
    this.commands.load(state.commands);

    // Terrain first: construction, cars and bushes all judge themselves against
    // the ground, and the ground's version counter is what their caches compare.
    this.grid.load(state.terrain);
    this.construction.load(state.construction);
    this.houses.load(state.houses);
    this.economy.load(state.economy);
    this.cars.load(state.cars);
    this.foodSources.load(state.food);
    this.pheromones.load(state.pheromones);
    this.surfaceAnts.load(state.surfaceAnts);
    // Including which buildings were dark and for how many cycles. A district
    // restored with an empty shed set would hand every stalled site its power
    // back for free, and would forget what §10's migration is a sum over.
    this.power.load(state.power);
    // Including the weather. A district restored at rainfall 1 is a district
    // whose drought ended because somebody reloaded, which is exactly the kind
    // of silent difference a total snapshot exists to refuse.
    this.water.load(state.water);
    // After the terrain, and it does not re-seal anything: the cells came back
    // `SEALED` with the grid, and sealing them again here would bump the terrain
    // version and make every loaded district look freshly changed to the caches
    // that watch it.
    this.facilities.load(state.facilities);
    // And the prices it trades at. A district restored at the price of last
    // resort when it had been quoted twice that sells the same compute for half
    // the money, and the treasury's borrowing rate follows the posted yield
    // rather than whatever the config was left on.
    this.market.load(state.market);
    this.applyBondYield();
    // Including the strike, and including who works where. A district restored
    // with everybody back at their desks is a district whose labour dispute ended
    // because somebody reloaded — the same silent difference the weather above
    // would have been.
    this.labour.load(state.labour);
    // Including how long each household has been in the dark. A district
    // restored with that forgotten is one whose brownout ended because somebody
    // reloaded, and the families it would then keep are families whose departure
    // the season record already counted.
    this.welfare.load(state.welfare);
    // Including which cells are deeded and which bushes are still coming. The grid
    // above restored the hedgerows that have already come in (`TerrainSnapshot.
    // planted`), because a standing bush is the ground's fact; what this restores is
    // the promises and the ones still growing. A district reloaded with its
    // covenants forgotten is a district whose mayor's signature expired because
    // somebody pressed a key.
    this.mercy.load(state.mercy);
    // Including the three marks the bills and the tithe are subtractions against.
    // A district restored without them bills itself for every kilowatt it has ever
    // drawn and remits a tithe on the whole history of its own economy, in one
    // cycle, silently — the pool is one number and nothing announces where it went.
    this.fiscal.load(state.fiscal);
    // And the corridor, including the mark the tolls and the wear are subtractions
    // against. A district restored without it pays itself a toll on every truck
    // that has ever crossed it and wears its roads to nothing in one cycle — the
    // same failure mode as the three above, on a number that is not money.
    this.freight.load(state.freight);
    // The Chronicle is restored, not replayed. A district picked up mid-epoch
    // has published what it published, and nothing can recompute that from the
    // state it arrives in.
    this.seasons.load(state.season);

    // The entrances follow the cells the snapshot just restored. The portals
    // themselves were built for whatever district this instance started as.
    this.portalSystem.syncPortalPositions(this.portalCells, this.portals);
  }

  /** Highest tick whose commands have been applied, or -1 before the world starts. */
  get commandsAppliedThrough(): number {
    return this.commands.appliedThrough;
  }

  /** Convenience: hash of `canonicalState()`. */
  stateHash(): string {
    return hashState(this.canonicalState());
  }

  /** How many colonies this surface has entrances for. */
  static readonly COLONY_COUNT = COLONY_COUNT;
}
